<template>
  <!-- 2026-07-22: `invoice-style-<name>` (see styleName() below) skins the
       shared markup for modern/corporate/minimal/compact. 2026-07-23:
       `contract-staffing` / `bajaj` / `reliance` additionally branch a few
       section wrappers (letterhead / parties / meta / bottom / sign /
       footer) so each layout can match a specific reference sheet — data
       bindings stay the same; only DOM structure for those sections
       differs. -->
  <div class="generic-invoice-preview border rounded bg-white" :class="'invoice-style-' + styleName">
    <!-- 2026-07-22 (professional-polish pass): `p-2` used to sit here as a
         Bootstrap padding utility — Bootstrap's utilities are all `!important`
         (`.p-2{padding:0.5rem!important}`), which silently defeated every
         per-style `.invoice-style-<name> .invoice-content{padding:...}`
         override written below (they compiled fine, just never actually
         took effect against `!important`). Removed here — the same class
         removal for every style, so this doesn't reintroduce a per-style
         markup difference — and padding is now fully controlled by the
         plain (non-important) `.invoice-content` rule in the scoped
         styles, so the density differences between styles are real. -->
    <div class="invoice-content" :style="rootAccentStyle">
      <!--
        Watermark lives INSIDE .invoice-content (above sheet paint, low
        opacity) so every style — Bajaj/Old, Reliance/SS5, Contract Staffing,
        Corporate/Modern/Compact — shows it in preview and html2canvas PDF.
        Previously it sat behind an opaque white content layer and was
        effectively invisible on specialty layouts.
      -->
      <img
        v-if="watermarkLogoUrl"
        :src="watermarkLogoUrl"
        alt=""
        aria-hidden="true"
        class="invoice-logo-watermark"
      />
      <!--
        2026-07-20: letterhead reworked to logo-left / "TAX INVOICE"-right on
        one row, instead of the previous logo+company-name-and-address row
        followed by a SEPARATE centered "TAX INVOICE" heading further down.
        The company name/address (and email/website) moved into the Bill
        From block below, where they belong per the reference invoice layout
        anyway — this just removes the redundant, awkward duplicate mention
        beside the logo without losing that information.

        2026-07-22: logo visibility + position, and the accent color, are
        now template-driven (config.layout.showLogo/logoPosition/
        primaryColor — see TemplateEditor.vue's Layout tab) instead of
        always hardcoded logo-left — these were being collected by the
        editor but silently dropped by this component. `letterheadClasses`/
        `rootAccentStyle` below implement that without touching the
        surrounding structure other callers (TemplateGalleryModal.vue's
        thumbnail, InvoiceCreateManual.vue's live panel) already rely on.
      -->
      <!-- ── Reliance form shell (SH1690) — pdf2htmlEX reference is a form
           PNG + absolute text overlays (no portable CSS). Rebuilt as one
           continuous black-grid sheet: logo | seller | "Original for
           Recipient" → light-blue Tax Invoice bar → meta (PO/POS on right). ── -->
      <div v-if="isReliance" class="rl-form">
        <table class="rl-sheet" cellspacing="0" cellpadding="0">
          <colgroup>
            <col class="rl-col-a" />
            <col class="rl-col-b" />
            <col class="rl-col-c" />
          </colgroup>
          <tbody>
            <tr class="rl-hdr">
              <td class="rl-hdr-logo">
                <img
                  v-if="showLogo"
                  :src="resolvedLogoUrl"
                  alt="Sattva Human Private Limited"
                  class="invoice-letterhead-logo"
                  @error="onLogoError"
                />
              </td>
              <td class="rl-hdr-co">
                <div class="rl-co-name">{{ (billFrom.companyName || 'Sattva Human Private Limited').toUpperCase() }}</div>
                <div class="rl-co-addr invoice-address-multiline">{{ billFrom.address || '—' }}</div>
                <div v-if="billFrom.phone" class="rl-co-meta">Mo. No.{{ billFrom.phone }}</div>
                <div class="rl-co-meta">GSTIN: {{ billFrom.gstin || '—' }}</div>
              </td>
              <td class="rl-hdr-orig">Original for Recipient</td>
            </tr>
            <!-- Blank bordered rows before/after light-blue Tax Invoice banner -->
            <tr class="rl-spacer rl-blank-row">
              <td colspan="3" class="rl-spacer-cell" aria-hidden="true">&nbsp;</td>
            </tr>
            <tr>
              <td colspan="3" class="rl-title-bar invoice-title">
                {{ documentType || 'Tax Invoice' }}
              </td>
            </tr>
            <tr class="rl-spacer rl-blank-row">
              <td colspan="3" class="rl-spacer-cell" aria-hidden="true">&nbsp;</td>
            </tr>
            <tr>
              <td colspan="2" class="rl-meta-cell">
                Invoice No : <b>{{ invoiceMeta.invoiceId || 'SH000' }}</b>
              </td>
              <td class="rl-meta-cell">Transport Mode: <b>—</b></td>
            </tr>
            <tr>
              <td colspan="2" class="rl-meta-cell">Invoice Date : <b>{{ formattedInvoiceDate }}</b></td>
              <td class="rl-meta-cell">Vehicle number: <b>—</b></td>
            </tr>
            <tr>
              <td colspan="2" class="rl-meta-cell">Reverse Charge (Y/N): <b>No</b></td>
              <td class="rl-meta-cell">Date of Supply : <b>{{ invoiceMeta.billingMonth || '—' }}</b></td>
            </tr>
            <tr>
              <!-- State | Code as separate bordered cells (left), PO on right —
                   matches SH1690 form columns (not one cell with float). -->
              <td colspan="2" class="rl-meta-split-cell">
                <table class="rl-meta-split" cellspacing="0" cellpadding="0">
                  <colgroup>
                    <col class="rl-meta-state-col" />
                    <col class="rl-meta-code-col" />
                  </colgroup>
                  <tbody>
                    <tr>
                      <td class="rl-meta-cell">State: <b>{{ sellerStateLabel }}</b></td>
                      <td class="rl-meta-cell">Code: <b>{{ stateCodeOf(billFrom.gstin) }}</b></td>
                    </tr>
                  </tbody>
                </table>
              </td>
              <td class="rl-meta-cell">PO No / WO No : <b>{{ filledPoNumber || '—' }}</b></td>
            </tr>
            <tr>
              <td colspan="2" class="rl-meta-cell rl-meta-empty"></td>
              <td class="rl-meta-cell">Place of Supply : <b>{{ invoiceMeta.placeOfSupply || '—' }}</b></td>
            </tr>
            <tr v-if="filledPackageCode">
              <td colspan="2" class="rl-meta-cell rl-meta-empty"></td>
              <td class="rl-meta-cell">Pkg Code : <b>{{ filledPackageCode }}</b></td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- ── Bajaj / OLD Template (SH1693) — logo top-right outside the
           double-border sheet; centered "Tax Invoice" title inside. ── -->
      <div v-else-if="isBajaj" class="bj-letterhead">
        <img
          v-if="showLogo"
          :src="resolvedLogoUrl"
          alt="Sattva Human Private Limited"
          class="invoice-letterhead-logo bj-logo"
          @error="onLogoError"
        />
      </div>

      <div
        v-else
        class="d-flex align-items-center invoice-letterhead pb-2 mb-2"
        :class="letterheadClasses"
      >
        <!-- Letterhead logo — a "must" per product direction: never blank/
             broken. Falls back through logoUrl prop → billFrom.logoUrl →
             the app's own bundled default asset (imported below), so this
             renders a real image in every context this component is used
             (side-by-side live panel, full-screen modal, and
             TemplateManager.vue's template preview). Hidden only when the
             template explicitly turns "Show Logo" off. -->
        <img
          v-if="showLogo"
          :src="resolvedLogoUrl"
          alt="Sattva Human Private Limited"
          class="invoice-letterhead-logo"
          @error="onLogoError"
        />
        <div class="invoice-title-wrap">
          <h5 class="invoice-title mb-0">{{ (documentType || 'Tax Invoice').toUpperCase() }}</h5>
        </div>
      </div>

      <!-- ── Contract Staffing parties (reference `.grid` / `.box`) ──
           Style-only markup: same billFrom/billTo bindings as the shared
           row below. Ship From/To is folded into the box titles (reference
           has no separate ship row). Email/website stay out of the box —
           they render in the CS footer like the reference sheet. -->
      <div v-if="isContractStaffing" class="cs-parties-grid">
        <div class="cs-box">
          <div class="section-label">Bill From / Ship From</div>
          <div class="party-name">{{ billFrom.companyName || '—' }}</div>
          <p class="cs-box-addr invoice-address-multiline">{{ billFrom.address || '—' }}</p>
          <div class="cs-kv">
            <span>GST Registration No</span>
            <b>{{ billFrom.gstin || '—' }}</b>
          </div>
          <div class="cs-kv">
            <span>State / Code</span>
            <b>{{ stateCodeOf(billFrom.gstin) }}</b>
          </div>
        </div>
        <div class="cs-box">
          <div class="section-label">Bill To / Ship To</div>
          <div class="party-name">{{ billTo.name || '—' }}</div>
          <p class="cs-box-addr invoice-address-multiline">{{ billTo.address || '—' }}</p>
          <div class="cs-kv">
            <span>GST Registration No</span>
            <b>{{ billTo.gstin || '—' }}</b>
          </div>
          <div class="cs-kv">
            <span>State / Code · Place of Supply</span>
            <b>{{ stateCodeOf(billTo.gstin) }}{{ invoiceMeta.placeOfSupply ? ' · ' + invoiceMeta.placeOfSupply : '' }}</b>
          </div>
          <div v-if="filledPackageCode" class="cs-kv">
            <span>Pkg Code:</span>
            <b>{{ filledPackageCode }}</b>
          </div>
        </div>
      </div>

      <!-- Bajaj / OLD Template: full double-border sheet (title → parties →
           line items → totals → sign/bank) is rendered in one block below
           so the table sits inside the same border as the header boxes. -->

      <!-- ── Reliance parties (SH1690) — Bill to / Ship to in the same
           bordered sheet grid as the header (light-blue section titles). ── -->
      <div v-else-if="isReliance" class="rl-form rl-form-cont">
        <table class="rl-sheet" cellspacing="0" cellpadding="0">
          <tbody>
            <!-- Blank bordered row before Bill to / Ship to headers -->
            <tr class="rl-spacer rl-blank-row">
              <td colspan="2" class="rl-spacer-cell" aria-hidden="true">&nbsp;</td>
            </tr>
            <tr>
              <td class="rl-party-h">Bill to Party - Recipient</td>
              <td class="rl-party-h">Ship to Party - Delivery Address</td>
            </tr>
            <tr>
              <td class="rl-party-body">
                <div><span>Name:</span> <b>{{ billTo.name || '—' }}</b></div>
                <div><span>Address:</span></div>
                <div class="invoice-address-multiline">{{ billTo.address || '—' }}</div>
                <div><span>GSTIN:</span> {{ billTo.gstin || '—' }}</div>
                <div>
                  <span>State:</span> {{ invoiceMeta.placeOfSupply || '—' }}
                  <span class="rl-code">Code {{ stateCodeOf(billTo.gstin) }}</span>
                </div>
              </td>
              <td class="rl-party-body">
                <div><span>Name:</span> <b>{{ billTo.name || '—' }}</b></div>
                <div><span>Address:</span></div>
                <div class="invoice-address-multiline">{{ billTo.address || '—' }}</div>
                <div><span>GSTIN:</span> {{ billTo.gstin || '—' }}</div>
                <div>
                  <span>State:</span> {{ invoiceMeta.placeOfSupply || '—' }}
                  <span class="rl-code">Code {{ stateCodeOf(billTo.gstin) }}</span>
                </div>
              </td>
            </tr>
            <!-- Blank bordered row after party details (before line-items) -->
            <tr class="rl-spacer rl-blank-row">
              <td colspan="2" class="rl-spacer-cell" aria-hidden="true">&nbsp;</td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Bill From / Bill To — shared by modern/corporate/minimal/compact.
           Each card carries its own State Code (2026-07-22) and, for Bill
           To only, Package Code when set. Bajaj skips this — its To/seller
           block lives inside `.bj-sheet` with the line items. -->
      <b-row v-else-if="!isBajaj" class="invoice-section invoice-parties-row pb-1 mb-1 small">
        <b-col md="6" class="invoice-col-divider">
          <div class="text-uppercase font-weight-bold mb-0 section-label">Bill From</div>
          <div class="font-weight-bold party-name">{{ billFrom.companyName || '—' }}</div>
          <div class="invoice-party-secondary invoice-address-multiline">{{ billFrom.address || '—' }}</div>
          <div v-if="billFrom.email || billFrom.website" class="invoice-party-secondary">
            <span v-if="billFrom.email">{{ billFrom.email }}</span>
            <span v-if="billFrom.email && billFrom.website"> &nbsp;|&nbsp; </span>
            <span v-if="billFrom.website">{{ billFrom.website }}</span>
          </div>
          <div>GSTIN: {{ billFrom.gstin || '—' }}</div>
          <div>State Code: {{ stateCodeOf(billFrom.gstin) }}</div>
        </b-col>
        <b-col md="6">
          <div class="text-uppercase font-weight-bold mb-0 section-label">Bill To</div>
          <div class="font-weight-bold party-name">{{ billTo.name || '—' }}</div>
          <div class="invoice-party-secondary invoice-address-multiline">{{ billTo.address || '—' }}</div>
          <div>GSTIN: {{ billTo.gstin || '—' }}</div>
          <div>State Code: {{ stateCodeOf(billTo.gstin) }}</div>
          <div v-if="filledPackageCode">Pkg Code: {{ filledPackageCode }}</div>
        </b-col>
      </b-row>

      <!-- Ship From / Ship To — not rendered for specialty styles (folded
           into their own party blocks above). -->
      <b-row v-if="!isSpecialtyLayout" class="invoice-section invoice-ship-row pb-1 mb-1 small">
        <b-col md="6" class="invoice-col-divider">
          <div class="text-uppercase font-weight-bold mb-0 section-label">Ship From</div>
          <div>{{ billFrom.companyName || '—' }}</div>
          <div class="invoice-party-secondary invoice-address-multiline">{{ billFrom.address || '—' }}</div>
        </b-col>
        <b-col md="6">
          <div class="text-uppercase font-weight-bold mb-0 section-label">Ship To</div>
          <div>{{ billTo.name || '—' }}</div>
          <div class="invoice-party-secondary invoice-address-multiline">{{ billTo.address || '—' }}</div>
        </b-col>
      </b-row>

      <!-- Contract Staffing meta — reference `.meta`: one right-aligned
           row of Ref Number / Invoice No / Date cells. Place of Supply
           lives in the Bill To kv row above (as in the reference). -->
      <div v-if="isContractStaffing" class="cs-meta">
        <div v-if="filledPoNumber" class="cs-meta-cell">
          <label>Ref Number</label>
          <div class="cs-meta-val">{{ filledPoNumber }}</div>
        </div>
        <div class="cs-meta-cell">
          <label>Invoice No</label>
          <div class="cs-meta-val">
            {{ invoiceMeta.invoiceId || 'SH000' }}
          </div>
        </div>
        <div class="cs-meta-cell">
          <label>Date</label>
          <div class="cs-meta-val">{{ formattedInvoiceDate }}</div>
        </div>
      </div>

      <!-- Reliance meta lives in the form header table above. -->
      <!-- Bajaj meta is inlined in the seller block above. -->
      <template v-else-if="!isBajaj && !isReliance">
        <!-- Invoice No / Place of Supply -->
        <b-row class="invoice-section invoice-meta-row pb-1 mb-1 small">
          <b-col md="6" class="invoice-col-divider">
            <strong>Invoice No:</strong> {{ invoiceMeta.invoiceId || 'SH000' }}
          </b-col>
          <b-col md="6"><strong>Place of Supply:</strong> {{ invoiceMeta.placeOfSupply || '—' }}</b-col>
        </b-row>

        <!-- Ref/PO Number (only when filled) + Date -->
        <b-row class="invoice-section invoice-meta-row pb-1 mb-2 small">
          <b-col v-if="filledPoNumber" md="6" class="invoice-col-divider"><strong>Ref / PO Number:</strong> {{ filledPoNumber }}</b-col>
          <b-col md="6"><strong>Date:</strong> {{ formattedInvoiceDate }}</b-col>
        </b-row>
      </template>

      <!-- No `responsive` prop here (deliberately removed 2026-07-21): that
           option wraps the table in a scrollable `.table-responsive` div,
           which is fine for an interactive UI table but wrong for a
           document — a horizontal scrollbar means whatever's scrolled out
           of view gets silently clipped out of the exported PDF too (see
           pdf-export.js's neutralizeClipping, which is a belt-and-suspenders
           fallback, not a reason to keep relying on scroll here). Instead,
           previewFields() below hands every column a percentage width that
           always sums to 100%, combined with `table-layout: fixed` in the
           scoped styles, so the table can never grow wider than its
           container regardless of column count — long content wraps within
           its own column instead of forcing an overflow. -->
      <!-- Bajaj embeds its own line-items table inside `.bj-sheet` below. -->
      <!-- :key forces a thead remount when Edit Columns changes label/order/
           visibility — Bootstrap-Vue can reuse header cells by field.key and
           leave stale labels when only the label/order of the same keys change. -->
      <invoice-preview-items-table
        v-if="!isBajaj && !useSecondLineItemsTable"
        :key="'lit-' + previewFieldsKey"
        class="invoice-line-items-table"
        :items="previewLineItems"
        :fields="previewFields"
        table-class="mb-0 invoice-line-items-table"
        :show-optional-titles="showOptionalTitles"
        :footer-row="isReliance ? relianceTotalFooterRow : null"
      >
        <template v-if="isReliance" slot="after-total">
          <tr class="rl-words-tax-row">
            <td class="rl-words-cell" :colspan="relianceWordsColspan">
              <div class="rl-words-label">Total Invoice amount in words</div>
              <div class="rl-words-val">{{ totalInWordsText }}</div>
            </td>
            <td class="rl-tot-stack" :colspan="relianceTaxColspan">
              <table class="rl-tot-inner" cellspacing="0" cellpadding="0">
                <colgroup>
                  <col class="rl-tot-label-col" />
                  <col class="rl-tot-val-col" />
                </colgroup>
                <tbody>
                  <template v-for="row in totalsRows">
                    <tr
                      v-if="row.isTotal && !row.isReverseCharge"
                      :key="row.label + '-sp-before'"
                      class="rl-spacer"
                    >
                      <td colspan="2" class="rl-spacer-cell" aria-hidden="true">&nbsp;</td>
                    </tr>
                    <tr :key="row.label">
                      <td :class="['rl-tot-label', { 'rl-tot-strong': row.isTotal && !row.isReverseCharge }]">{{ row.label }}</td>
                      <td :class="['rl-tot-val', { 'rl-tot-strong': row.isTotal && !row.isReverseCharge }]">{{ formatCurrency(row.value) }}</td>
                    </tr>
                  </template>
                </tbody>
              </table>
            </td>
          </tr>
        </template>
      </invoice-preview-items-table>
      <div v-else-if="!isBajaj && useSecondLineItemsTable" class="invoice-li-multi-tables">
        <div
          v-for="group in lineItemTableGroups"
          :key="'lit-g-' + group.key"
          class="invoice-li-table-group mb-2"
        >
          <div class="invoice-li-table-heading">{{ group.heading }}</div>
          <invoice-preview-items-table
            :key="'lit-' + group.key + '-' + previewFieldsKeyForGroup(group.key)"
            class="invoice-line-items-table"
            :items="group.items"
            :fields="previewFieldsForGroup(group.key)"
            table-class="mb-0 invoice-line-items-table"
            :show-optional-titles="showOptionalTitlesForGroup(group.key)"
            :footer-row="isReliance && isLastLineItemGroup(group) ? relianceTotalFooterRow : null"
          >
            <template v-if="isReliance && isLastLineItemGroup(group)" slot="after-total">
              <tr class="rl-words-tax-row">
                <td class="rl-words-cell" :colspan="relianceWordsColspan">
                  <div class="rl-words-label">Total Invoice amount in words</div>
                  <div class="rl-words-val">{{ totalInWordsText }}</div>
                </td>
                <td class="rl-tot-stack" :colspan="relianceTaxColspan">
                  <table class="rl-tot-inner" cellspacing="0" cellpadding="0">
                    <colgroup>
                      <col class="rl-tot-label-col" />
                      <col class="rl-tot-val-col" />
                    </colgroup>
                    <tbody>
                      <template v-for="row in totalsRows">
                        <tr
                          v-if="row.isTotal && !row.isReverseCharge"
                          :key="row.label + '-sp-before'"
                          class="rl-spacer"
                        >
                          <td colspan="2" class="rl-spacer-cell" aria-hidden="true">&nbsp;</td>
                        </tr>
                        <tr :key="row.label">
                          <td :class="['rl-tot-label', { 'rl-tot-strong': row.isTotal && !row.isReverseCharge }]">{{ row.label }}</td>
                          <td :class="['rl-tot-val', { 'rl-tot-strong': row.isTotal && !row.isReverseCharge }]">{{ formatCurrency(row.value) }}</td>
                        </tr>
                      </template>
                    </tbody>
                  </table>
                </td>
              </tr>
            </template>
          </invoice-preview-items-table>
        </div>
      </div>

      <!-- Billing-period note — config.layout.showBillingPeriodNote
           (2026-07-22): a dynamic "* Billing Invoice for <start> to <end>
           Month *" row spanning the line-items table's width.
           Off by default (see showBillingPeriodNote computed); Contract
           Staffing opts in today. Headcount line-items preset keeps this
           off so Old Template + Headcount does not inject the note above
           Doc Type. Any custom template can still opt in via the same
           layout flag. Derived entirely from invoiceMeta.billingMonth
           (the wizard's "Billing Month" field, e.g. "May-2026") — no
           separate note text needs to be authored for this to show. -->
      <!-- my-2 (2026-07-22), not the my-1 this shipped with — every other
           major section on this document (Bill To/From, Ship To/From,
           Total In Words, Notes...) keeps a ~0.5-0.75rem gap from its
           neighbors; my-1 (0.25rem) made this one specific row look
           noticeably more cramped than the rhythm established everywhere
           else, even though its own py-1 gives it comfortable internal
           padding. -->
      <div v-if="billingPeriodLabel" class="small text-center font-weight-bold invoice-billing-period-note my-2 py-1">
        {{ billingPeriodLabel }}
      </div>

      <div
        v-if="!isBajaj && filledDescription"
        class="text-center invoice-description-note my-2 py-1"
      >
        <strong>{{ filledDescription }}</strong>
      </div>

      <!-- ── Contract Staffing bottom (reference `.bottom`) ──
           Left: GST / SAC·HSN / LUT / PAN kv rows + cream bank box.
           Right: Sub-Total / GST rows + light grand total + amount in words.
           Signature + contact footer are separate blocks below (`.sign` /
           `footer`), matching the reference sheet order. -->
      <template v-if="isContractStaffing">
        <div class="cs-bottom">
          <div class="cs-reg">
            <div class="cs-kvrow">
              <span>GST Registration No</span>
              <span>{{ billFrom.gstin || '—' }}</span>
            </div>
            <div class="cs-kvrow">
              <span>SAC / HSN Code</span>
              <span>{{ sacHsnCode || '—' }}</span>
            </div>
            <div v-if="lutNoDisplay" class="cs-kvrow">
              <span>LUT No.</span>
              <span>{{ lutNoDisplay }}</span>
            </div>
            <div class="cs-kvrow">
              <span>PAN No</span>
              <span>{{ billFrom.pan || '—' }}</span>
            </div>
            <div v-if="showBankDetails" class="cs-bank invoice-bank-box">
              <b>Details for Payment</b><br>
              <template v-if="bankDetails.bankName">Bank Name: <b>{{ bankDetails.bankName }}</b></template>
              <template v-if="bankDetails.accountType"> · Account Type: <b>{{ bankDetails.accountType }}</b></template>
              <br v-if="bankDetails.bankName || bankDetails.accountType">
              <template v-if="bankDetails.accountName">Account Name: <b>{{ bankDetails.accountName }}</b><br></template>
              <template v-if="bankDetails.accountNumber">Account No: <b>{{ bankDetails.accountNumber }}</b></template>
              <template v-if="bankDetails.ifscCode"> · IFSC Code: <b>{{ bankDetails.ifscCode }}</b></template>
            </div>
          </div>
          <div class="cs-totals">
            <div
              v-for="row in totalsRows"
              :key="row.label"
              :class="row.isTotal ? 'cs-grand' : 'cs-trow'"
            >
              <span>{{ row.label }}</span>
              <span>{{ formatCurrency(row.value) }}</span>
            </div>
            <div class="cs-words">{{ totalInWordsText }}</div>
          </div>
        </div>

        <div class="cs-sign">
          <div class="cs-forco">
            For<br>
            <b>{{ billFrom.companyName || 'Sattva Human Private Limited' }}</b>
          </div>
          <div class="cs-signline">
            <!-- Reserved DSC / wet-signature area — keep clear of totals + footer. -->
            <div class="signature-box">
              <img v-if="signatureUrl" :src="signatureUrl" alt="Signature" class="signature-image" />
            </div>
            <div class="cs-signline-rule" />
            Authorised Signatory
          </div>
        </div>

        <div v-if="showTerms && termsText" class="small invoice-terms mb-2 pb-1">
          <strong>Notes:</strong>
          <span class="invoice-terms-body">{{ termsText }}</span>
        </div>

        <div class="invoice-contact-footer cs-footer">
          <span class="invoice-contact-left">{{ contactWebsite }}</span>
          <span v-if="footerText" class="invoice-contact-footer-center">{{ footerText }}</span>
          <span class="invoice-contact-right">{{ contactEmail }}</span>
        </div>
      </template>

      <!-- ── Bajaj / OLD Template (SH1693) — full double-border sheet ──
           Logo sits above/outside (`.bj-letterhead`); this sheet holds
           title → To|seller → items → tax rows → Total Amount / words →
           For+sign+GST/SAC/PAN | yellow bank box. Contact footer is
           outside the border (reference places email/web under the frame). ── -->
      <template v-else-if="isBajaj">
        <div class="bj-sheet">
          <div class="bj-title invoice-title">
            {{ documentType || 'Tax Invoice' }}
          </div>
          <div class="bj-top">
            <table class="bj-party-table bj-seller" cellspacing="0" cellpadding="0">
              <colgroup>
                <col class="bj-party-label-col" />
                <col class="bj-party-val-col" />
              </colgroup>
              <thead>
                <tr>
                  <th colspan="2" class="bj-party-head">
                    <span class="bj-from-label">Bill From</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="bj-party-label">Company</td>
                  <td class="bj-party-val bj-hl">{{ billFrom.companyName || '—' }}</td>
                </tr>
                <tr>
                  <td class="bj-party-label">Address</td>
                  <td class="bj-party-val invoice-address-multiline">{{ billFrom.address || '—' }}</td>
                </tr>
                <tr>
                  <td class="bj-party-label">State / Code</td>
                  <td class="bj-party-val">{{ sellerStateLabel }} / {{ stateCodeOf(billFrom.gstin) }}</td>
                </tr>
                <tr>
                  <td class="bj-party-label">Invoice No</td>
                  <td class="bj-party-val bj-hl">
                    {{ invoiceMeta.invoiceId || 'SH000' }}
                  </td>
                </tr>
                <tr>
                  <td class="bj-party-label">Date</td>
                  <td class="bj-party-val bj-hl">{{ formattedInvoiceDate }}</td>
                </tr>
              </tbody>
            </table>
            <table class="bj-party-table bj-to" cellspacing="0" cellpadding="0">
              <colgroup>
                <col class="bj-party-label-col" />
                <col class="bj-party-val-col" />
              </colgroup>
              <thead>
                <tr>
                  <th colspan="2" class="bj-party-head">
                    <span class="bj-to-label">Bill To</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td class="bj-party-label">Company</td>
                  <td class="bj-party-val bj-hl">{{ billTo.name || '—' }}</td>
                </tr>
                <tr>
                  <td class="bj-party-label">Address</td>
                  <td class="bj-party-val invoice-address-multiline">{{ billTo.address || '—' }}</td>
                </tr>
                <tr>
                  <td class="bj-party-label">GSTIN</td>
                  <td class="bj-party-val">{{ billTo.gstin || '—' }}</td>
                </tr>
                <tr>
                  <td class="bj-party-label">Place of Supply</td>
                  <td class="bj-party-val">{{ invoiceMeta.placeOfSupply || '—' }}</td>
                </tr>
                <tr v-if="filledPoNumber">
                  <td class="bj-party-label">Ref / PO Number</td>
                  <td class="bj-party-val bj-hl">{{ filledPoNumber }}</td>
                </tr>
                <tr v-if="filledPackageCode">
                  <td class="bj-party-label">Pkg Code</td>
                  <td class="bj-party-val bj-hl">{{ filledPackageCode }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <!-- Items grid + Sub-Total / GST / Total Amount / Total in Words as
               one spaced block (gap above header row + below words row). -->
          <div class="bj-items-block">
            <template v-if="!useSecondLineItemsTable">
              <invoice-preview-items-table
                :key="'bj-lit-' + previewFieldsKey"
                class="invoice-line-items-table bj-items-table"
                :items="previewLineItems"
                :fields="previewFields"
                table-class="mb-0 invoice-line-items-table bj-items-table"
                :show-optional-titles="showOptionalTitles"
              />
            </template>
            <div v-else class="invoice-li-multi-tables">
              <div
                v-for="group in lineItemTableGroups"
                :key="'bj-lit-g-' + group.key"
                class="invoice-li-table-group mb-2"
              >
                <div class="invoice-li-table-heading">{{ group.heading }}</div>
                <invoice-preview-items-table
                  :key="'bj-lit-' + group.key + '-' + previewFieldsKeyForGroup(group.key)"
                  class="invoice-line-items-table bj-items-table"
                  :items="group.items"
                  :fields="previewFieldsForGroup(group.key)"
                  table-class="mb-0 invoice-line-items-table bj-items-table"
                  :show-optional-titles="showOptionalTitlesForGroup(group.key)"
                />
              </div>
            </div>

            <table class="bj-sum-table" cellspacing="0" cellpadding="0">
              <tbody>
                <tr
                  v-for="row in bajajTaxRows"
                  :key="row.label"
                  :class="{ 'bj-sum-total': row.isTotal }"
                >
                  <td class="bj-sum-label">{{ row.label }}</td>
                  <td class="bj-sum-val" :class="{ 'bj-hl': row.isTotal }">{{ formatCurrency(row.value) }}</td>
                </tr>
                <tr v-if="filledDescription" class="bj-sum-desc">
                  <td colspan="2" class="bj-sum-desc-text"><strong>{{ filledDescription }}</strong></td>
                </tr>
                <tr class="bj-sum-words">
                  <td colspan="2" class="bj-sum-words-text">Total in Words: {{ totalInWordsText }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div class="bj-bottom">
            <div class="bj-reg">
              <div class="bj-for bj-hl">
                For {{ billFrom.companyName || 'Sattva Human Private Limited' }}
              </div>
              <div class="bj-sign">
                <div class="signature-box">
                  <img v-if="signatureUrl" :src="signatureUrl" alt="Signature" class="signature-image" />
                </div>
                <div class="bj-sign-label">Authorised Signatory</div>
              </div>
              <!-- Real 2-col table (not span/b rows) so the vertical rule
                   stays aligned and empty SAC shows "—" in its own cell. -->
              <table class="bj-kv-table" cellspacing="0" cellpadding="0">
                <colgroup>
                  <col class="bj-kv-label-col" style="width:48%" />
                  <col class="bj-kv-val-col" style="width:52%" />
                </colgroup>
                <tbody>
                  <tr>
                    <td class="bj-kv-label">GST Registration No:</td>
                    <td class="bj-kv-val bj-hl">{{ billFrom.gstin || '—' }}</td>
                  </tr>
                  <tr>
                    <td class="bj-kv-label">SAC / HSN Code</td>
                    <td class="bj-kv-val bj-hl">{{ sacHsnCode || '—' }}</td>
                  </tr>
                  <tr v-if="lutNoDisplay">
                    <td class="bj-kv-label">LUT No.</td>
                    <td class="bj-kv-val bj-hl">{{ lutNoDisplay }}</td>
                  </tr>
                  <tr>
                    <td class="bj-kv-label">PAN No:</td>
                    <td class="bj-kv-val bj-hl">{{ billFrom.pan || '—' }}</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <div v-if="showBankDetails" class="bj-bank invoice-bank-box bj-hl">
              <div class="bj-bank-title">Details for Payment:</div>
              <div v-if="bankDetails.bankName"><span>Bank Name:</span> {{ bankDetails.bankName }}</div>
              <div v-if="bankDetails.accountType"><span>Account Type:</span> {{ bankDetails.accountType }}</div>
              <div v-if="bankDetails.accountName"><span>Account Name:</span> {{ bankDetails.accountName }}</div>
              <div v-if="bankDetails.accountNumber"><span>Account No:</span> {{ bankDetails.accountNumber }}</div>
              <div v-if="bankDetails.ifscCode"><span>IFSC Code:</span> {{ bankDetails.ifscCode }}</div>
            </div>
          </div>

          <div v-if="showTerms && termsText" class="small invoice-terms bj-terms mb-2 pb-1">
            <strong>Notes:</strong>
            <span class="invoice-terms-body">{{ termsText }}</span>
          </div>
        </div>
        <div class="invoice-contact-footer bj-footer">
          <span class="invoice-contact-left">{{ contactWebsite }}</span>
          <span class="invoice-contact-right">{{ contactEmail }}</span>
        </div>
      </template>

      <!-- ── Reliance bottom (SH1690 / SS5) — words | tax stack (incl. reverse
           charge under Total after Tax), then 3 cols: bank | Common Seal
           (signatory-style bottom stack) | cert+open signatory, then Notes. ── -->
      <template v-else-if="isReliance">
        <div class="rl-form rl-form-cont">
          <!-- Bank | Common Seal | cert+signatory stay a 3-col sheet.
               Words | tax now live in the line-items <tfoot> so their
               vertical rule shares the items column grid. -->
          <table class="rl-sheet" cellspacing="0" cellpadding="0">
            <colgroup>
              <col class="rl-bottom-bank-col" />
              <col class="rl-bottom-seal-col" />
              <col class="rl-bottom-sign-col" />
            </colgroup>
            <tbody>
              <tr class="rl-spacer">
                <td colspan="3" class="rl-spacer-cell" aria-hidden="true">&nbsp;</td>
              </tr>
              <tr>
                <td class="rl-bank-cell">
                  <div v-if="showBankDetails" class="rl-bank-block">
                    <div class="rl-sec-h">Bank Details</div>
                    <div v-if="bankDetails.accountNumber">Bank A/C: {{ bankDetails.accountNumber }}</div>
                    <div v-if="bankDetails.ifscCode">Bank IFSC: {{ bankDetails.ifscCode }}</div>
                  </div>
                </td>
                <td class="rl-seal-cell">
                  <div class="rl-seal-space" aria-hidden="true" />
                  <div class="rl-seal-label">Common Seal</div>
                </td>
                <td class="rl-sign-cell">
                  <div class="rl-cert">Certified that the particulars given above are true and correct</div>
                  <div class="rl-forco">For {{ billFrom.companyName || 'Company Name' }}</div>
                  <div class="rl-sign-area">
                    <img v-if="signatureUrl" :src="signatureUrl" alt="Signature" class="signature-image" />
                  </div>
                  <div class="rl-sign-label">Authorised signatory</div>
                </td>
              </tr>
              <tr v-if="showTerms && termsText">
                <td colspan="3" class="rl-notes-cell">
                  <div class="rl-terms-block">
                    <div class="rl-sec-h">Notes</div>
                    <div class="invoice-terms-body">{{ termsText }}</div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div class="invoice-contact-footer rl-footer">
          <span class="invoice-contact-left">{{ contactWebsite }}</span>
          <span class="invoice-contact-right">{{ contactEmail }}</span>
        </div>
      </template>

      <template v-else>
        <!-- Sub-Total → CGST/SGST/IGST → Total Amount -->
        <b-row class="mb-0 mt-1">
          <b-col md="6" offset-md="6">
            <b-table-simple small bordered class="mb-0 invoice-totals-table">
              <b-tbody>
                <b-tr v-for="row in totalsRows" :key="row.label" :class="{ 'font-weight-bold total-amount-row': row.isTotal }">
                  <b-td>{{ row.label }}</b-td>
                  <b-td class="text-right">{{ formatCurrency(row.value) }}</b-td>
                </b-tr>
              </b-tbody>
            </b-table-simple>
          </b-col>
        </b-row>

        <!-- Total In Words — Indian Lakh/Crore numbering (number-to-words.js). -->
        <div class="small total-in-words mb-2 pb-1">
          <strong>Total In Words:</strong> {{ totalInWordsText }}
        </div>

        <!-- Footer: bank details (left) + seller declarations + signature (right). -->
        <b-row class="invoice-footer small">
          <b-col md="6" class="invoice-col-divider">
            <div v-if="showBankDetails" class="invoice-bank-box">
              <h6 class="text-uppercase section-label font-weight-bold mb-1">Details for Payment</h6>
              <div v-if="bankDetails.bankName">Bank Name: {{ bankDetails.bankName }}</div>
              <div v-if="bankDetails.accountName">Account Name: {{ bankDetails.accountName }}</div>
              <div v-if="bankDetails.accountNumber">Account No: {{ bankDetails.accountNumber }}</div>
              <div v-if="bankDetails.ifscCode">IFSC Code: {{ bankDetails.ifscCode }}</div>
            </div>
          </b-col>
          <b-col md="6">
            <div class="mb-1">For {{ billFrom.companyName || 'Sattva Human Private Limited' }}</div>
            <div>GST Registration No: {{ billFrom.gstin || '—' }}</div>
            <div v-if="sacHsnCode">SAC / HSN Code: {{ sacHsnCode }}</div>
            <div v-if="lutNoDisplay">LUT No.: {{ lutNoDisplay }}</div>

            <!-- Reserved DSC area (~120px) so a digital stamp never overlaps
                 totals / Total In Words / the contact footer below. -->
            <div class="signature-box ml-auto">
              <img v-if="signatureUrl" :src="signatureUrl" alt="Signature" class="signature-image" />
            </div>
            <div class="font-weight-bold mt-1 text-right authorised-signatory-label">Authorised Signatory</div>
          </b-col>
        </b-row>

        <div v-if="showTerms && termsText" class="small invoice-terms mb-2 pb-1">
          <strong>Notes:</strong>
          <span class="invoice-terms-body">{{ termsText }}</span>
        </div>

        <div class="invoice-contact-footer">
          <span class="invoice-contact-left">{{ contactWebsite }}</span>
          <span v-if="footerText" class="invoice-contact-footer-center">{{ footerText }}</span>
          <span class="invoice-contact-right">{{ contactEmail }}</span>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import { BRow, BCol, BTableSimple, BTbody, BTr, BTd } from 'bootstrap-vue'
import InvoicePreviewItemsTable from './InvoicePreviewItemsTable.vue'
// Same bundled Sattva branding asset used app-wide (nav-bar logo, and the
// default attached to a submitted invoice's own payload — see
// InvoiceCreateManual.vue's companyLogoPreview/sattvaDefaultLogo). Used here
// as the final fallback so the letterhead always shows a real logo image,
// never a missing/broken one, regardless of which caller renders this
// component or whether a location-specific logoUrl was ever configured.
import sattvaDefaultLogo from '@/assets/images/logo/logo.svg'
// Icon-only diamond mark (no "Sattva" wordmark) — watermark asset only;
// letterhead still uses the full logo via resolvedLogoUrl / sattvaDefaultLogo.
import sattvaLogoIcon from '@/assets/images/logo/logo-icon.svg'
import moment from 'moment'
import { amountInWords } from '@/utils/number-to-words'
import {
  formatCurrency as formatCurrencyUtil,
  formatLineItemCellValue,
  isLineItemMoneyColumn,
  isLineItemNumericColumn,
} from '@/utils/currency-format'
import { dedupeSacHsnColumns, isSacHsnColumn, resolveSacHsnCode, resolveLutNo, SAC_HSN_LABEL, supportsOptionalLineItemTitle } from '@/utils/invoice-calculation-engine'
import invoiceDateCalculator from '@/utils/invoice-date-calculator'

/** Fallback thead labels when a column's label is blank after Edit Columns. */
const DEFAULT_LINE_ITEM_HEADER_BY_KEY = {
  srNo: 'Sr.No',
  itemName: 'Item Details',
  sacCode: SAC_HSN_LABEL,
  quantity: 'Quantity',
  unitPrice: 'Rate',
  amount: 'Amount',
  employeeId: 'Employee ID',
  employeeName: 'Employee Name',
  candidateName: 'Candidate Name',
  baseAmount: 'Base Amount',
  rateCard: 'Rate Card',
  presentDays: 'Present Days',
  totalHeadCount: 'Total Head Count',
  monthToDayConversion: 'Month to Day conversion',
}

function resolvePreviewHeaderLabel(col) {
  if (!col) return ''
  const custom = String(col.label || '').trim()
  if (custom) return custom
  return DEFAULT_LINE_ITEM_HEADER_BY_KEY[col.key] || col.key || ''
}

export default {
  name: 'GenericInvoicePreview',
  components: { BRow, BCol, BTableSimple, BTbody, BTr, BTd, InvoicePreviewItemsTable },
  props: {
    template: { type: Object, default: () => ({}) },
    // Optional explicit column list — takes priority over template.lineItemColumns
    // when provided. InvoiceCreateManual.vue passes its own
    // `lineItemValidationColumns` (the same dynamicColumnConfig-aware source
    // that drives the actual editable Item Table in WizardLineItemsStep.vue),
    // so whatever the user has added/removed/renamed via Manage Columns shows
    // up here too — the template's own static lineItemColumns are only used
    // as a fallback for callers that don't track live column edits (e.g.
    // TemplateManager.vue's own template preview, which has no per-invoice
    // Manage Columns state to reflect in the first place).
    columns: { type: Array, default: () => null },
    /** Optional Table 2 columns when the second line-items table uses a different config. */
    columnsTable2: { type: Array, default: () => null },
    // Optional explicit override — InvoiceCreateManual.vue passes its own
    // companyLogoPreview computed (formData.invoiceSattvaInfo.logoUrl, with
    // its own fallback to the same default asset). Left blank by other
    // callers (e.g. TemplateManager.vue's template preview), which is fine —
    // resolvedLogoUrl() below still resolves to a real image either way.
    logoUrl: { type: String, default: '' },
    // Optional branding watermark — falls back to bundled icon-only mark.
    watermarkUrl: { type: String, default: '' },
    // Optional signature image (e.g. a stored digital-signature asset URL).
    // No caller wires this yet — there's no signature-upload feature in this
    // app today — but the reserved box below always renders regardless, so
    // this is ready to light up the moment one exists.
    signatureUrl: { type: String, default: '' },
    billFrom: {
      type: Object,
      default: () => ({
        companyName: 'Sattva Human Private Limited',
        address: 'Office, 1st Floor, Ruia Chambers, Hadapsar, Pune 411028',
        gstin: '27ABDCS0586N1ZT',
        pan: 'ABDCS0586N',
        sacCode: '',
        lutNo: '',
        email: '',
        website: '',
      }),
    },
    billTo: {
      type: Object,
      default: () => ({
        name: 'Sample Client Pvt Ltd',
        address: 'Client address',
        gstin: '29XXXXX0000X1Z5',
      }),
    },
    invoiceMeta: {
      type: Object,
      default: () => ({
        invoiceId: 'SH000',
        invoiceDate: '2026-06-01',
        placeOfSupply: 'Maharashtra',
        // Combined Ref/PO Number — see InvoiceCreateManual.vue's
        // previewInvoiceMeta computed for why this one field now covers
        // both concepts.
        poNumber: '',
        packageCode: '',
        // e.g. "May-2026" — see InvoiceCreateManual.vue's previewInvoiceMeta
        // and billingPeriodLabel() below.
        billingMonth: '',
      }),
    },
    lineItems: {
      type: Array,
      default: () => ([
        { srNo: 1, itemName: 'Manpower Supply', title: 'Manpower Supply', quantity: 1, unitPrice: 19582, amount: 19582 },
      ]),
    },
    totals: {
      type: Object,
      default: () => ({
        subtotal: 19582,
        sgst: 0,
        cgst: 0,
        igst: 3524.76,
        total: 23106.76,
      }),
    },
    bankDetails: {
      type: Object,
      default: () => ({
        bankName: 'HDFC Bank',
        accountName: 'Sattva Human Private Limited',
        accountNumber: '50200046864261',
        ifscCode: 'HDFC0000486',
        accountType: 'Current Account',
      }),
    },
    documentType: { type: String, default: 'Tax Invoice' },
    // Optional invoice-level description/narration (WizardMoreDetailsStep
    // "Description" field → formData.description / API `description`).
    description: { type: String, default: '' },
    // "Show decimals" toggle (see WizardLineItemsStep.vue's Amount Summary
    // switch, wired through InvoiceCreateManual.vue) — defaults to true so
    // every other caller (e.g. TemplateManager.vue's own template preview,
    // which has no such toggle) keeps the original always-2-decimal display.
    showDecimals: { type: Boolean, default: true },
    showCurrencySymbol: { type: Boolean, default: true },
    useThousandSeparators: { type: Boolean, default: true },
    useSecondLineItemsTable: { type: Boolean, default: false },
    lineItemTableHeading1: { type: String, default: '' },
    lineItemTableHeading2: { type: String, default: '' },
    // Invoice workflow status (e.g. Raised / Revised). Kept for callers /
    // list-workflow wiring; printable sheet does not show a REVISED badge.
    // invoiceId / invoice number is never changed here. Callers may also
    // put status on invoiceMeta.
    status: { type: String, default: '' },
  },
  computed: {
    // Printed/PDF invoice date — DD-MON-YYYY (e.g. 23-JUL-2026). Form inputs
    // keep ISO; only the sheet display uses this. Empty/invalid → em dash.
    formattedInvoiceDate() {
      const raw = this.invoiceMeta && this.invoiceMeta.invoiceDate
      return invoiceDateCalculator.formatInvoiceDisplayDate(raw) || '—'
    },
    // Trimmed PO / package — empty string when unset so templates can v-if
    // and avoid rendering "—" placeholders for optional reference fields.
    filledPoNumber() {
      const v = this.invoiceMeta && this.invoiceMeta.poNumber
      return v != null && String(v).trim() !== '' ? String(v).trim() : ''
    },
    filledPackageCode() {
      const v = this.invoiceMeta && this.invoiceMeta.packageCode
      return v != null && String(v).trim() !== '' ? String(v).trim() : ''
    },
    filledDescription() {
      const fromProp = this.description != null ? String(this.description).trim() : ''
      if (fromProp) return fromProp
      const meta = this.invoiceMeta && this.invoiceMeta.description
      return meta != null && String(meta).trim() !== '' ? String(meta).trim() : ''
    },
    sacHsnCode() {
      return resolveSacHsnCode(this.billFrom) || ''
    },
    lutNoDisplay() {
      return resolveLutNo(this.billFrom) || ''
    },
    // Fallback chain: explicit `logoUrl` prop → `billFrom.logoUrl` (in case a
    // caller only ever passes billFrom) → the bundled default asset. Also
    // drops back to the bundled default if whichever URL we tried actually
    // failed to load (see onLogoError/logoLoadFailed) — the letterhead must
    // never render a broken image.
    resolvedLogoUrl() {
      if (!this.logoLoadFailed) {
        const url = this.logoUrl || (this.billFrom && this.billFrom.logoUrl)
        if (url) return url
      }
      return sattvaDefaultLogo
    },
    // Prefer explicit prop → template.layout branding → bundled icon-only mark.
    // Never use the letterhead wordmark so the watermark stays a clean brand
    // diamond regardless of which logoUrl the letterhead is showing.
    watermarkLogoUrl() {
      const fromProp = String(this.watermarkUrl || '').trim()
      if (fromProp) return fromProp
      const layout = (this.template && this.template.layout) || {}
      const fromLayout = String(
        layout.watermarkUrl
        || layout.watermark
        || layout.WATERMARK
        || '',
      ).trim()
      if (fromLayout) return fromLayout
      return sattvaLogoIcon
    },
    showBankDetails() {
      return !(this.template.layout && this.template.layout.showBankDetails === false)
    },
    // ── Layout settings wiring (2026-07-22) ──────────────────────────────
    // config.layout is authored in TemplateEditor.vue's Layout tab (Style,
    // Primary Colour, Logo Position, Show Logo/Bank Details/Terms toggles,
    // Footer Text, Terms Text) — every field below except `showBankDetails`
    // (already wired, see above) used to be collected and persisted but
    // never actually read anywhere, so changing them in the editor had zero
    // visible effect on the rendered invoice/PDF.
    //
    // `style` (2026-07-22 — closes backend-handoff/INVOICE_TEMPLATES_API_DOCUMENTATION.md §7
    // Known Gap #2) is now branched on too, but ONLY in the scoped <style>
    // block below via the `.invoice-style-<name>` class the template's root
    // div carries — see styleName(). Each of the 4 values TemplateEditor.vue's
    // dropdown offers (modern/corporate/minimal/compact) is a genuinely
    // distinct, ground-up design system (2026-07-22 redesign — see the "══
    // Design styles" section in the scoped <style> block below): typography
    // (font stack, weight, letter-spacing), color/accent usage, letterhead
    // treatment, section-header styling, table header/row/border treatment,
    // spacing/density, divider style, corner radius, and shadows all vary
    // per style — but strictly as a CSS skin. None of them change what data
    // renders or add/remove/reorder any content — same markup, same v-ifs,
    // just different paint — so nothing here can reintroduce the
    // table-overflow/PDF-clipping bugs fixed earlier (those live in the DOM
    // structure + pdf-export.js, untouched by this).
    styleName() {
      const raw = this.template.layout && this.template.layout.style
      // 'standard' (2026-07-22 rename → 'modern'): any template/localStorage
      // data saved before this rename still has the old value — treat it as
      // its direct successor rather than silently falling through to the
      // "unrecognized value" branch below (same end result today since
      // 'bajaj' IS the fallback, but keeping this explicit means it stays
      // correct even if the fallback style ever changes later).
      const s = raw === 'standard' ? 'modern' : raw
      // Specialty styles with style-conditional markup (2026-07-23):
      // contract-staffing / bajaj / reliance — see matching
      // `.invoice-style-*` blocks in the scoped styles below.
      // 'minimal' removed from selectable styles — fall back like unknown values.
      return ['corporate', 'compact', 'modern', 'contract-staffing', 'bajaj', 'reliance'].includes(s) ? s : 'bajaj'
    },
    // True only for the Contract Staffing layout — drives the style-
    // conditional markup branches above (parties / meta / bottom / sign /
    // footer). Other styles keep the shared Bootstrap-row structure.
    isContractStaffing() {
      return this.styleName === 'contract-staffing'
    },
    isBajaj() {
      return this.styleName === 'bajaj'
    },
    isReliance() {
      return this.styleName === 'reliance'
    },
    // Any style that replaces the shared Bill/Ship/meta Bootstrap rows.
    isSpecialtyLayout() {
      return this.isContractStaffing || this.isBajaj || this.isReliance
    },
    // Seller "State:" label for the Reliance meta grid — derived from the
    // GSTIN state code (27 → Maharashtra) so callers don't need a separate
    // field. Falls back to a dash when the GSTIN is missing/unrecognized.
    sellerStateLabel() {
      const code = this.stateCodeOf(this.billFrom && this.billFrom.gstin)
      const map = {
        '27': 'Maharashtra',
        '29': 'Karnataka',
        '07': 'Delhi',
        '09': 'Uttar Pradesh',
        '33': 'Tamil Nadu',
        '24': 'Gujarat',
        '06': 'Haryana',
        '03': 'Punjab',
        '08': 'Rajasthan',
        '36': 'Telangana',
        '37': 'Andhra Pradesh',
        '32': 'Kerala',
        '19': 'West Bengal',
      }
      return map[code] || (code !== '—' ? code : '—')
    },
    showLogo() {
      return !(this.template.layout && this.template.layout.showLogo === false)
    },
    showTerms() {
      return !(this.template.layout && this.template.layout.showTerms === false)
    },
    logoPosition() {
      return (this.template.layout && this.template.layout.logoPosition) || 'left'
    },
    footerText() {
      return (this.template.layout && this.template.layout.footerText) || ''
    },
    // Unified contact footer — every style: left = website, right = email.
    // Defaults keep corners correct when billFrom.email/website are empty.
    contactEmail() {
      return (this.billFrom && this.billFrom.email) || 'connect@sattvahuman.com'
    },
    contactWebsite() {
      return (this.billFrom && this.billFrom.website) || 'www.sattvahuman.com'
    },
    termsText() {
      return (this.template.layout && this.template.layout.termsText) || ''
    },
    showBillingPeriodNote() {
      return !!(this.template.layout && this.template.layout.showBillingPeriodNote)
    },
    // "* Billing Invoice for 21 April to 20 May 2026 Month *" — the wizard's
    // billing-cycle convention is the 21st of the PRECEDING month through
    // the 20th of the selected Billing Month (matches the reference
    // invoice this was built against). Returns '' (renders nothing) when
    // the template hasn't opted into this note or no Billing Month has
    // been picked yet, rather than showing a note with a blank/invalid
    // date range.
    billingPeriodLabel() {
      if (!this.showBillingPeriodNote) return ''
      const raw = this.invoiceMeta && this.invoiceMeta.billingMonth
      if (!raw) return ''
      const periodEnd = moment(raw, 'MMM-YYYY')
      if (!periodEnd.isValid()) return ''
      periodEnd.date(20)
      const periodStart = periodEnd.clone().subtract(1, 'month').date(21)
      // Contract Staffing reference omits the leading/trailing asterisks
      // used by the Headcount Billing note; same date range either way.
      if (this.isContractStaffing) {
        return `Billing Invoice for ${periodStart.format('D MMMM')} to ${periodEnd.format('D MMMM YYYY')} Month`
      }
      return `* Billing Invoice for ${periodStart.format('D MMMM')} to ${periodEnd.format('D MMMM YYYY')} Month *`
    },
    // Reversing the flex order (rather than moving the DOM nodes) keeps the
    // logo's @error/onLogoError wiring and the title's own markup untouched
    // — 'right' mirrors the row, 'center' stacks logo-above-title. Anything
    // else (including the 'modern' style's default row layout) keeps the
    // original logo-left/title-right row.
    letterheadClasses() {
      if (this.logoPosition === 'center') {
        return 'flex-column justify-content-center text-center'
      }
      if (this.logoPosition === 'right') {
        return 'flex-row-reverse justify-content-between'
      }
      return 'justify-content-between'
    },
    // `primaryColor` is exposed as a CSS custom property (rather than
    // inline-styling every colored element individually) so the scoped
    // <style> block below stays the single source of truth for exactly
    // which elements pick up the brand color, and gracefully falls back to
    // the original hardcoded colors via `var(--invoice-primary-color, ...)`
    // when a template doesn't set one (e.g. both built-in samples).
    rootAccentStyle() {
      const color = this.template.layout && this.template.layout.primaryColor
      return color ? { '--invoice-primary-color': color } : {}
    },
    // Signature of the live column set — used as b-table :key so Edit Columns
    // label/order/add/remove changes always remount the thead (see template).
    previewFieldsKey() {
      // Include type + order index so Edit Columns type/reorder changes
      // remount thead even when key+label stay the same.
      return this.previewFields
        .map((f, i) => `${i}:${f.key}:${f.label}:${f._colType || ''}`)
        .join('|')
    },
    /** Table 2 fields when columnsTable2 is provided; else same as previewFields. */
    previewFieldsTable2() {
      if (this.columnsTable2 && this.columnsTable2.length) {
        return this.buildPreviewFieldsFromColumns(this.columnsTable2)
      }
      return this.previewFields
    },
    previewFields() {
      const rawCols = (this.columns && this.columns.length) ? this.columns : (this.template.lineItemColumns || [])
      return this.buildPreviewFieldsFromColumns(rawCols)
    },
    previewSourceColumns() {
      return (this.columns && this.columns.length) ? this.columns : (this.template.lineItemColumns || [])
    },
    showOptionalTitles() {
      return supportsOptionalLineItemTitle(this.previewSourceColumns)
    },
    showOptionalTitlesTable2() {
      if (this.columnsTable2 && this.columnsTable2.length) {
        return supportsOptionalLineItemTitle(this.columnsTable2)
      }
      return this.showOptionalTitles
    },
    previewLineItems() {
      return this.lineItems.map((item, idx) => ({
        srNo: idx + 1,
        ...item,
      }))
    },
    lineItemTableGroups() {
      const all = this.previewLineItems
      if (!this.useSecondLineItemsTable) {
        return [{ key: 1, heading: '', items: all }]
      }
      const h1 = String(this.lineItemTableHeading1 || '').trim() || 'Table 1'
      const h2 = String(this.lineItemTableHeading2 || '').trim() || 'Table 2'
      const g1 = []
      const g2 = []
      all.forEach((item) => {
        if (Number(item.tableGroup) === 2) g2.push(item)
        else g1.push(item)
      })
      return [
        { key: 1, heading: h1, items: g1 },
        { key: 2, heading: h2, items: g2 },
      ]
    },
    // Sum of the primary amount column currently on the items table
    // (lineTotal → amount → baseAmount → taxableValue), falling back to
    // totals.subtotal / totals.total. Used by the Reliance Total bar under
    // the grid so it matches the live columns, not a stale hardcoded field.
    itemsTableTotal() {
      const items = this.previewLineItems || []
      if (this.useSecondLineItemsTable && this.columnsTable2 && this.columnsTable2.length) {
        return this.lineItemTableGroups.reduce((sum, group) => {
          const fields = this.previewFieldsForGroup(group.key) || []
          const fieldKeys = new Set(fields.map((f) => f.key))
          const prefer = ['lineTotal', 'amount', 'baseAmount', 'taxableValue']
          let groupTotal = 0
          let found = false
          for (let i = 0; i < prefer.length; i += 1) {
            const k = prefer[i]
            if (!fieldKeys.has(k)) continue
            found = true
            groupTotal = (group.items || []).reduce((s, row) => s + (parseFloat(row && row[k]) || 0), 0)
            break
          }
          return sum + (found ? groupTotal : 0)
        }, 0)
      }
      const fieldKeys = new Set((this.previewFields || []).map((f) => f.key))
      const prefer = ['lineTotal', 'amount', 'baseAmount', 'taxableValue']
      for (let i = 0; i < prefer.length; i += 1) {
        const k = prefer[i]
        if (!fieldKeys.has(k)) continue
        return items.reduce((sum, row) => sum + (parseFloat(row && row[k]) || 0), 0)
      }
      const t = this.totals || {}
      if (t.subtotal != null) return t.subtotal
      if (t.total != null) return t.total
      return 0
    },
    // Last visible items-table fields — Total row + words/tax footer share
    // this colgroup so their vertical split matches the line-item grid.
    relianceGridFields() {
      if (this.useSecondLineItemsTable && this.lineItemTableGroups && this.lineItemTableGroups.length) {
        const last = this.lineItemTableGroups[this.lineItemTableGroups.length - 1]
        return this.previewFieldsForGroup(last.key)
      }
      return this.previewFields
    },
    relianceGridColCount() {
      return (this.relianceGridFields && this.relianceGridFields.length) || 1
    },
    // Index of the first tax-rate column (IGST Rate / CGST / SGST).
    // Words occupy [0, index); tax stack occupies [index, end) so labels
    // start under the rate column (e.g. "18%") and amounts stay in the
    // remaining money columns (IGST Amount + Total Invoice Amount).
    relianceSplitIndex() {
      const fields = this.relianceGridFields || []
      const rateKeys = ['igstRate', 'cgstRate', 'sgstRate']
      let idx = fields.findIndex((f) => f && rateKeys.includes(f.key))
      if (idx < 0) {
        idx = fields.findIndex((f) => /(?:igst|cgst|sgst)\s*rate/i.test(String((f && f.label) || '')))
      }
      if (idx < 0) {
        const amountKeys = ['igstAmount', 'cgstAmount', 'sgstAmount']
        idx = fields.findIndex((f) => f && amountKeys.includes(f.key))
        if (idx < 0) {
          idx = fields.findIndex((f) => /igst\s*amount|cgst\s*amount|sgst\s*amount/i.test(String((f && f.label) || '')))
        }
      }
      if (idx < 0) {
        const moneyIdxs = fields.map((f, i) => {
          const k = String((f && f.key) || '').toLowerCase()
          const cls = String((f && f.class) || '')
          const isMoney = cls.includes('invoice-col-money') && !/rate|price/.test(k)
          return isMoney ? i : -1
        }).filter((i) => i >= 0)
        if (moneyIdxs.length >= 2) idx = moneyIdxs[moneyIdxs.length - 2]
        else if (moneyIdxs.length === 1) idx = moneyIdxs[0]
        else idx = Math.max(fields.length - 1, 1)
      }
      const max = Math.max(fields.length - 1, 1)
      return Math.min(Math.max(idx, 1), max)
    },
    relianceWordsColspan() {
      return this.relianceSplitIndex
    },
    relianceTaxColspan() {
      return Math.max(this.relianceGridColCount - this.relianceSplitIndex, 1)
    },
    // Visible line-item column sums — Total row, words, and tax stack all
    // read from this so they cannot drift from the grid the user sees.
    relianceLineColumnSums() {
      const items = this.previewLineItems || []
      const sumKey = (key) => items.reduce((s, row) => s + (parseFloat(row && row[key]) || 0), 0)
      return {
        taxableValue: sumKey('taxableValue'),
        baseAmount: sumKey('baseAmount'),
        igstAmount: sumKey('igstAmount'),
        cgstAmount: sumKey('cgstAmount'),
        sgstAmount: sumKey('sgstAmount'),
        lineTotal: sumKey('lineTotal'),
        amount: sumKey('amount'),
      }
    },
    relianceTotalFooterRow() {
      const fields = this.relianceGridFields || []
      const sums = this.relianceLineColumnSums
      const keys = new Set(fields.map((f) => f && f.key))
      const moneyKeys = new Set(['igstAmount', 'cgstAmount', 'sgstAmount', 'lineTotal'])
      if (!keys.has('lineTotal')) moneyKeys.add('amount')
      if (keys.has('taxableValue')) moneyKeys.add('taxableValue')
      else if (keys.has('baseAmount')) moneyKeys.add('baseAmount')
      const values = {}
      fields.forEach((f) => {
        if (f && moneyKeys.has(f.key)) {
          values[f.key] = this.formatCurrency(sums[f.key] || 0)
        }
      })
      return { label: 'Total', values }
    },
    // Sub-Total → CGST → SGST → IGST → Total Amount.
    // Reliance (SH1690 / SS5) uses "before tax / Add: IGST / after tax /
    // reverse charge" wording — reverse charge sits in this tax stack (not
    // only in the signatory cell) and is shown only on that template.
    // Amounts come from the visible line-item columns (not header GST ×
    // subtotal) so words + tax stack match the grid Total row.
    totalsRows() {
      const t = this.totals || {}
      let subtotal = t.subtotal || 0
      let total = t.total != null ? t.total : subtotal
      let igst = t.igst
      let cgst = t.cgst
      let sgst = t.sgst
      const reverseCharge = t.reverseCharge != null ? t.reverseCharge : 0
      if (this.isReliance) {
        const sums = this.relianceLineColumnSums
        const fieldKeys = new Set((this.relianceGridFields || []).map((f) => f && f.key))
        if (fieldKeys.has('taxableValue') || sums.taxableValue) subtotal = sums.taxableValue || sums.baseAmount || subtotal
        else if (fieldKeys.has('baseAmount') || sums.baseAmount) subtotal = sums.baseAmount || subtotal
        if (fieldKeys.has('igstAmount')) igst = sums.igstAmount
        if (fieldKeys.has('cgstAmount')) cgst = sums.cgstAmount
        if (fieldKeys.has('sgstAmount')) sgst = sums.sgstAmount
        total = (fieldKeys.has('lineTotal') ? sums.lineTotal : this.itemsTableTotal) || total
      }
      const pct = (val) => (subtotal && val != null ? Math.round((val / subtotal) * 100) : 0)
      const hasAmt = (val) => val != null && Number(val) !== 0
      if (this.isReliance) {
        const rows = [{ label: 'Total Amount before Tax', value: subtotal }]
        if (hasAmt(igst) && !hasAmt(cgst) && !hasAmt(sgst)) {
          rows.push({ label: `Add: IGST(${pct(igst)}%)`, value: igst })
        } else {
          if (hasAmt(cgst)) rows.push({ label: `Add: CGST(${pct(cgst)}%)`, value: cgst })
          if (hasAmt(sgst)) rows.push({ label: `Add: SGST(${pct(sgst)}%)`, value: sgst })
          if (hasAmt(igst)) rows.push({ label: `Add: IGST(${pct(igst)}%)`, value: igst })
        }
        rows.push({ label: 'Total Amount after Tax:', value: total, isTotal: true })
        rows.push({ label: 'GST on Reverse Charge', value: reverseCharge, isReverseCharge: true })
        return rows
      }
      const rows = [{ label: 'Sub-Total', value: subtotal }]
      if (hasAmt(t.cgst)) rows.push({ label: `CGST (${pct(t.cgst)}%)`, value: t.cgst })
      if (hasAmt(t.sgst)) rows.push({ label: `SGST (${pct(t.sgst)}%)`, value: t.sgst })
      if (hasAmt(t.igst)) rows.push({ label: `IGST (${pct(t.igst)}%)`, value: t.igst })
      rows.push({ label: 'Total Amount', value: total, isTotal: true })
      return rows
    },
    // Bajaj / OLD Template (SH1693): Sub-Total → SGST → CGST → IGST →
    // Total Amount (reference order; yellow highlight on the total value).
    // GST on Reverse Charge is Reliance-only — not shown here.
    bajajTaxRows() {
      const t = this.totals || {}
      const subtotal = t.subtotal || 0
      const total = t.total != null ? t.total : subtotal
      const pct = (val) => (subtotal && val != null ? Math.round((val / subtotal) * 100) : 0)
      return [
        { label: 'Sub-Total', value: subtotal },
        { label: `SGST (${pct(t.sgst)}%)`, value: t.sgst != null ? t.sgst : 0 },
        { label: `CGST (${pct(t.cgst)}%)`, value: t.cgst != null ? t.cgst : 0 },
        { label: `IGST (${pct(t.igst)}%)`, value: t.igst != null ? t.igst : 0 },
        { label: 'Total Amount', value: total, isTotal: true },
      ]
    },
    totalInWordsText() {
      const t = this.totals || {}
      // Reliance: speak the same grand total as the line-item Total row
      // (sum of Total Invoice Amount), not a separately computed header GST.
      const n = this.isReliance
        ? this.itemsTableTotal
        : (t.total != null ? t.total : (t.subtotal || 0))
      return amountInWords(n)
    },
  },
  data() {
    return {
      // Set true if whatever URL we tried to load 404s/fails — forces the
      // fallback below to the bundled asset, which (being a local, always-
      // deployed import rather than a remote/backend-driven URL) can't
      // itself fail the same way.
      logoLoadFailed: false,
    }
  },
  watch: {
    // Give a newly-selected location's logo a fresh chance to load instead
    // of staying stuck on the fallback after a previous one failed.
    logoUrl() {
      this.logoLoadFailed = false
    },
  },
  methods: {
    // Shared with WizardLineItemsStep.vue — see src/utils/currency-format.js.
    // Wraps the util with the "Show decimals" toggle's current state.
    formatCurrency(val) {
      return formatCurrencyUtil(val, this.showDecimals ? 2 : 0, {
        showCurrencySymbol: this.showCurrencySymbol,
        useThousandSeparators: this.useThousandSeparators,
      })
    },
    previewFieldsForGroup(groupKey) {
      return Number(groupKey) === 2 ? this.previewFieldsTable2 : this.previewFields
    },
    showOptionalTitlesForGroup(groupKey) {
      return Number(groupKey) === 2 ? this.showOptionalTitlesTable2 : this.showOptionalTitles
    },
    previewFieldsKeyForGroup(groupKey) {
      return this.previewFieldsForGroup(groupKey)
        .map((f, i) => `${i}:${f.key}:${f.label}:${f._colType || ''}`)
        .join('|')
    },
    isLastLineItemGroup(group) {
      const groups = this.lineItemTableGroups || []
      const last = groups[groups.length - 1]
      return !!(last && group && Number(group.key) === Number(last.key))
    },
    buildPreviewFieldsFromColumns(rawCols) {
      const filteredCols = dedupeSacHsnColumns(
        (rawCols || []).filter((c) => {
          if (!c || !c.key) return false
          if (c.type === 'move' || c.type === 'actions') return false
          if (c.visible === false) return false
          if (!c.label && !c.type) return false
          return true
        }),
      )
      const cols = filteredCols.length
        ? filteredCols
        : [
          { key: 'srNo', label: 'Sr.No', type: 'text' },
          { key: 'itemName', label: 'Particulars', type: 'text' },
          { key: 'quantity', label: 'Qty', type: 'number' },
          { key: 'unitPrice', label: 'Rate', type: 'currency' },
          { key: 'amount', label: 'Amount', type: 'currency' },
        ]
      const widths = this.columnWidthPercents(cols)
      const moneyDec = this.showDecimals ? 2 : 0
      const fmtOpts = {
        decimals: moneyDec,
        showCurrencySymbol: this.showCurrencySymbol,
        useThousandSeparators: this.useThousandSeparators,
      }
      return cols.map((c, idx) => {
        const numeric = isLineItemNumericColumn(c)
        const money = isLineItemMoneyColumn(c)
        const hsn = isSacHsnColumn(c) || c.type === 'sac-select'
        const isDesc = this.isDescriptionColumn(c)
        let alignClass = 'text-left'
        if (hsn) alignClass = 'text-center'
        else if (money) alignClass = 'text-right'
        else if (numeric) alignClass = 'text-center'
        const cellKind = money ? 'invoice-col-money' : (numeric ? 'invoice-col-num' : 'invoice-col-text')
        // Description / Candidate Name / Particulars: bold thead always; body
        // cells only when the cell has content (empty placeholders stay normal).
        const descHeaderClass = isDesc ? 'invoice-col-desc font-weight-bold' : ''
        const headerLabel = resolvePreviewHeaderLabel(c)
        return {
          key: c.key,
          label: headerLabel,
          _colType: c.type || '',
          class: [alignClass, cellKind, descHeaderClass].filter(Boolean).join(' '),
          thClass: [alignClass, 'invoice-li-th', cellKind, descHeaderClass].filter(Boolean).join(' '),
          tdClass: (value) => {
            const base = [alignClass, 'invoice-li-td', cellKind]
            if (isDesc && String(value == null ? '' : value).trim() !== '') {
              base.push('invoice-col-desc', 'font-weight-bold')
            }
            return base.filter(Boolean).join(' ')
          },
          thStyle: { width: widths[idx] },
          formatter: (val) => formatLineItemCellValue(val, c, fmtOpts),
        }
      })
    },
    isDescriptionColumn(col = {}) {
      const key = String(col.key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
      const label = String(col.label || '').toLowerCase()
      return (
        key === 'itemname'
        || key === 'title'
        || key === 'description'
        || key === 'descriptionofgoods'
        || key === 'particulars'
        || key === 'candidatename'
        || key === 'employeename'
        || /description|particular|item\s*name|employee\s*name|candidate\s*name/.test(label)
      )
    },
    onLogoError() {
      this.logoLoadFailed = true
    },
    // GST state code = the first two digits of a GSTIN (e.g. '27ABDCS0586N1ZT'
    // -> '27' = Maharashtra). Derived here rather than requiring a separate
    // "state code" field/prop from every caller, since it's fully determined
    // by the GSTIN they already pass in.
    stateCodeOf(gstin) {
      const match = /^(\d{2})/.exec(String(gstin || '').trim())
      return match ? match[1] : '—'
    },
    // Relative "weight" per column type — see previewFields()'s thStyle
    // comment. Narrow, short-content columns (Sr.No, Qty-like) get a small
    // weight, free-text description columns get a large one, everything
    // else is "normal". Converted to a percentage (of the total weight
    // across all currently-visible columns) in columnWidthPercents() below,
    // so the resulting widths always sum to 100% regardless of how many
    // columns there are.
    columnWeight(key, label) {
      const k = String(key || '').toLowerCase().replace(/[^a-z0-9]/g, '')
      const l = String(label || '').toLowerCase()
      if (/\bsr\.?\s*no\.?\b|\bsl\.?\s*no\.?\b|serial\s*no/.test(l) || ['srno', 'sno', 'slno'].includes(k)) {
        return 0.6
      }
      if (['quantity', 'qty', 'doj', 'bu', 'geo', 'band', 'payoutrate', 'daytomonthratio', 'presentdays', 'totalbillabledays'].includes(k)) {
        return 0.9
      }
      // Short % columns — leave room for rupee totals on the right.
      if (['igstrate', 'cgstrate', 'sgstrate'].includes(k) || /^(igst|cgst|sgst)\s*rate$/.test(l.trim())) {
        return 0.95
      }
      // ₹ amounts (not rates) need room for symbol + lakhs + paise at 0.82em.
      if (
        ['igstamount', 'cgstamount', 'sgstamount', 'linetotal', 'taxablevalue', 'amount'].includes(k)
        || /igst\s*amount|cgst\s*amount|sgst\s*amount|invoice\s*amount|taxable\s*value/.test(l)
      ) {
        return 1.75
      }
      if (/particular|item\s*name|description|employee\s*name|candidate\s*name|remarks/.test(l)) {
        return 2.4
      }
      return 1.3
    },
    // Percentage width per column (parallel array, same order as `cols`)
    // that always sums to 100% — see the `table-layout: fixed` note in the
    // scoped styles for why this is what actually prevents overflow.
    columnWidthPercents(cols) {
      const weights = cols.map((c) => this.columnWeight(c.key, c.label || c.key))
      const total = weights.reduce((sum, w) => sum + w, 0) || 1
      return weights.map((w) => `${((w / total) * 100).toFixed(2)}%`)
    },
  },
}
</script>

<style scoped>
/* ══════════════════════════════════════════════════════════════════════
   STRUCTURE (shared across every style) — sizing mechanics, positioning,
   the table-overflow/PDF-clipping fixes, and the em-based scaling system.
   These rules never vary by config.layout.style; a handful carry a neutral
   fallback color/border ("safety net" — always overridden below, since
   styleName() only ever resolves to one of the 4 named styles, but keeps
   this file inert rather than broken if that ever changes). Everything
   that actually gives each style its identity — color/accent usage,
   letterhead/section-header/table treatment, dividers, corners, density —
   is defined per-style further down.

   2026-07-22 v2 (professional-polish pass — see the "DESIGN STYLES" header
   comment below for the full self-critique/rationale): the type scale here
   was tightened from the first redesign pass (fewer, closer font-sizes —
   real invoicing products like Stripe/QuickBooks/Xero lean on WEIGHT for
   hierarchy far more than size), and `.invoice-content`'s padding moved
   here from a `p-2` Bootstrap utility class that used to sit directly in
   the template — Bootstrap's spacing utilities are all `!important`
   (`.p-2 { padding: 0.5rem !important; }`), which silently defeated every
   per-style `.invoice-content` padding override written in the first
   redesign pass (they compiled fine, just never visually applied). Moving
   padding fully into this plain, non-important rule (with the `p-2` class
   removed from the template) means the density differences between styles
   set further down are now real. ══════════════════════════════════════ */
/* A4 page canvas (2026-07-23): fixed 210×297mm sheet so live preview,
   full-screen modal, and PDF export all share one true page geometry.
   Callers that need a smaller on-screen footprint (side panel, gallery
   thumb) scale this root with `transform: scale(...)` — they must NOT
   shrink `font-size` here, or the layout reflows into a cramped strip
   and the PDF no longer matches the readable A4 document. */
.generic-invoice-preview {
  position: relative;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-sizing: border-box;
  width: 210mm;
  max-width: 210mm;
  min-height: 297mm;
  margin: 0 auto;
  font-size: 11.5pt;
  line-height: 1.45;
  color: #141414;
  background-color: #fff;
  box-shadow:
    0 0.5mm 1.5mm rgba(0, 0, 0, 0.06),
    0 2mm 8mm rgba(0, 0, 0, 0.08);
}
/* Flex column that fills the A4 sheet (`flex: 1` under the root's
   `min-height: 297mm`) so `.invoice-contact-footer` can pin to the physical
   bottom via `margin-top: auto` when content is short. Grows with content
   past one page (no max-height) so multi-page PDF keeps the footer in flow.
   Avoid hard-coding `min-height: 297mm` here — styles like Bajaj add root
   padding for the outer logo and would otherwise exceed true A4 height. */
.invoice-content {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  flex: 1 1 auto;
  box-sizing: border-box;
  padding: 0.5rem;
  /* Transparent so the watermark (child, z-index above sheet paint) shows
     through table/section backgrounds that are also kept light. */
  background: transparent;
}

/* ── Watermark ─────────────────────────────────────────────────────────
   Inside .invoice-content, above sheet paint (z-index 2), very low opacity.
   Plain <img> so html2canvas captures it like any other element. Shown on
   EVERY style (Bajaj/Old, Reliance/SS5, Contract Staffing, Corporate…). */
.invoice-logo-watermark {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 48%;
  max-width: 300px;
  height: auto;
  opacity: 0.09;
  pointer-events: none;
  user-select: none;
  z-index: 2;
}

.invoice-address-multiline {
  white-space: pre-line;
}

.invoice-party-secondary {
  color: #3d3d3d;
}

.invoice-title-wrap {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.4em;
}

.invoice-letterhead {
  border-bottom: 1px solid #ddd;
}
.invoice-letterhead-logo {
  /* em, not px (2026-07-21): a caller can override .generic-invoice-preview's
     own font-size to render a denser/larger version of this same component
     (e.g. a compact side-panel vs. a full-screen modal) — every size in
     this stylesheet is expressed relative to that one font-size so the
     whole document scales together as a unit instead of some pieces (this
     logo, the signature box) staying visually oversized/undersized while
     the text around them shrinks or grows. 4em = 56px at this component's
     own default 0.875rem/14px base font-size. */
  height: 4em;
  width: auto;
  max-width: 140px;
  object-fit: contain;
  flex-shrink: 0;
}
/* 2026-07-22: logoPosition:"center" stacks the logo directly above the
   title via flex-column (see letterheadClasses()) — with no gap between
   them by default, the logo ends up touching the title text right below
   it. Only applies when actually stacked (flex-column), so the normal
   left/right row layouts — which already have breathing room from
   justify-content-between spreading the two ends of a wide row — are
   unaffected. */
.invoice-letterhead.flex-column .invoice-letterhead-logo {
  margin-bottom: 0.5em;
}
/* 2026-07-22 v2: 1.37em → 1.08em — a real invoice's document-type label
   ("TAX INVOICE") is confidently small, not shouty (see the DESIGN STYLES
   comment below); hierarchy now comes mainly from weight/letter-spacing/
   color per style, not from this being the biggest thing on the page. */
.invoice-title {
  display: inline-block;
  color: #1f1f1f;
  font-size: 1.08em;
}
/* 2026-07-22 v2: 0.8em → 0.72em, and every style below now keeps this as
   plain, uncoloured, unfilled text (no background chips/pills anywhere —
   see the DESIGN STYLES comment below on color restraint). */
.section-label {
  color: #4a4a4a;
  font-size: 0.82em;
  line-height: 1.3;
}
/* 2026-07-22 v2: 1.09em → 1em — company/client names are already bold
   (via the template's own `font-weight-bold` class) and don't need an
   extra size bump on top to read as emphasised; one less distinct size in
   the overall scale. */
.party-name {
  font-size: 1em;
}

/* Neutral divider defaults, lightened (2026-07-22 v2: #eee kept, but no
   style now darkens these into a heavier grid — see DESIGN STYLES below).
   Modern and Minimal remove them entirely in favour of whitespace. */
.invoice-section {
  border-bottom: 1px solid #eee;
  line-height: 1.4;
}
.invoice-col-divider {
  border-right: 1px solid #eee;
}

/* table-layout: fixed (2026-07-21) — combined with previewFields()'s
   percentage-based thStyle widths (which always sum to exactly 100%), this
   guarantees the table can never grow wider than its container, no matter
   how many columns a user adds via Manage Columns or how narrow the
   container is. Without `fixed`, the browser's automatic table layout lets
   a column's content (or an explicit min-width) push the table wider than
   100%, which — since this table used to have bootstrap-vue's `responsive`
   prop wrapping it in an `overflow-x: auto` div — meant extra columns could
   silently scroll out of view and get cut off in the exported PDF (which
   only rasterizes what's actually laid out, not what's scrolled off).
   word-break/overflow-wrap ensure a long unbroken value (e.g. a long
   employee/particulars string) wraps within its fixed-width column instead
   of forcing an overflow anyway. HARD CONSTRAINT (2026-07-22 redesign):
   every `.invoice-style-<name>` block below may repaint padding/border/
   background on these cells, but none of them may touch `table-layout` or
   the widths handed down from previewFields()/columnWidthPercents(). */
.invoice-line-items-table {
  table-layout: fixed;
  width: 100%;
  box-sizing: border-box;
  overflow: visible;
  /* Modest breathing room above/below the items grid (before Sub-Total /
     totals). Specialty layouts that need flush sheet seams (Reliance) or
     their own larger gaps (Contract Staffing, Bajaj) override below. */
  margin-top: 0.5rem;
  margin-bottom: 0.5rem;
}
.invoice-li-table-heading {
  font-weight: 700;
  font-size: 0.95em;
  padding: 0.35rem 0.4rem;
  margin-bottom: 0.15rem;
  background: #fff59d;
  display: inline-block;
  min-width: 4rem;
}
.invoice-line-items-table >>> caption.invoice-li-group-title,
.invoice-line-items-table >>> .invoice-li-group-title {
  caption-side: top !important;
  font-weight: 700 !important;
  text-align: left;
  background: transparent !important;
  letter-spacing: 0.01em;
  padding: 0.2rem 0.15rem 0.4rem;
  color: inherit;
}
.invoice-line-items-table >>> td.invoice-col-desc,
.invoice-line-items-table >>> .invoice-col-desc,
.invoice-line-items-table >>> thead th.invoice-col-desc {
  font-weight: 700 !important;
}
.invoice-line-items-table >>> th,
.invoice-line-items-table >>> td {
  vertical-align: middle;
  padding: 0.3rem 0.5rem;
  box-sizing: border-box;
  overflow: visible;
}
/* Headers: wrap at word boundaries only (readable multi-word labels like
   "Month to Day conversion") — never mid-glyph shatter via break-word. */
.invoice-line-items-table >>> thead th,
.invoice-line-items-table >>> th.invoice-li-th,
.invoice-line-items-table >>> .invoice-li-repeat-header-row > th {
  word-break: normal;
  overflow-wrap: break-word;
  white-space: normal;
  line-height: 1.25;
  hyphens: manual;
}
/* Text / particulars: wrap inside the fixed column. */
.invoice-line-items-table >>> td.invoice-col-text {
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: normal;
}
/* Currency / qty / % values stay on one line across EVERY style (Modern,
   Corporate, Minimal, Compact, Contract Staffing, Bajaj, Reliance). */
.invoice-line-items-table >>> td.invoice-col-money,
.invoice-line-items-table >>> td.invoice-col-num,
.invoice-line-items-table >>> td.text-right,
.invoice-line-items-table >>> td.text-center {
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}
.invoice-line-items-table >>> th.invoice-col-money,
.invoice-line-items-table >>> td.invoice-col-money {
  text-align: right !important;
}
.invoice-line-items-table >>> th.invoice-col-num,
.invoice-line-items-table >>> td.invoice-col-num {
  text-align: center !important;
}
.invoice-line-items-table >>> th.invoice-col-text,
.invoice-line-items-table >>> td.invoice-col-text {
  text-align: left !important;
}
/* Line-item VALUE cells: smaller than headers so amounts/text read cleaner.
   Per-style blocks may tighten further (~0.72em); headers stay untouched. */
.invoice-line-items-table >>> tbody td {
  font-size: 0.82em;
}
.invoice-line-items-table >>> .b-table-empty-row > td,
.invoice-line-items-table >>> tr.b-table-empty-row > td {
  text-align: center;
  color: #5e5873;
  font-style: italic;
  padding: 0.75rem 0.5rem;
  white-space: normal;
}
/* 2026-07-22: tabular-nums on both money tables so digits in every row
   render at a fixed width — with proportional digits (the default), a
   "1" is narrower than an "8", so a column of right-aligned amounts with
   different digits doesn't actually line up decimal-point-to-decimal-point
   even though every value is correctly right-aligned and correctly
   formatted. This only affects numeral glyph widths, not letters, so it's
   safe to apply table-wide rather than needing to target currency cells
   specifically. */
.invoice-line-items-table,
.invoice-totals-table {
  font-variant-numeric: tabular-nums;
}
/* 2026-07-22 v2: this used to default to the template's accent colour
   (`var(--invoice-primary-color, ...)`) — meaning every style's Total
   Amount row was accent-tinted whether that style actually wanted a colour
   touch there or not. Neutral dark by default now; only the 2 styles that
   deliberately choose the total FIGURE as their one accent touchpoint
   (Modern, Compact — see each block below) opt back into colour. Bumped
   from 1.08em → 1.3em and given real breathing room via padding so the
   total is unmistakably the largest, most prominent figure on the page
   through size/weight/whitespace alone, per the "no colour-block
   backgrounds needed" brief — no style fills this row with a solid colour
   anymore (Corporate used to; removed). */
.invoice-totals-table >>> .total-amount-row td {
  border-top: 2px solid #1f1f1f;
  font-size: 1.3em;
  padding-top: 0.5em;
  padding-bottom: 0.5em;
}
.invoice-billing-period-note {
  font-style: italic;
}
.invoice-description-note,
.invoice-description-note strong {
  font-family: inherit;
  font-weight: 700 !important;
  white-space: pre-wrap;
}
/* 2026-07-22 v2: 0.94em → 0.92em, part of the same tightened scale. */
.invoice-totals-table {
  font-size: 0.92em;
}
.invoice-totals-table >>> td {
  padding: 0.25rem 0.6rem;
  box-sizing: border-box;
  overflow: visible;
}
.invoice-totals-table >>> td:last-child {
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
}
.total-in-words {
  border-bottom: 1px solid #eee;
}
.invoice-terms {
  border-bottom: 1px solid #eee;
}
.invoice-terms-body {
  display: block;
  white-space: pre-line;
  margin-top: 0.15em;
}
.invoice-footer {
  padding-top: 0.75rem;
  margin-top: 0.35rem;
  line-height: 1.4;
}

/* Contact footer — constant across ALL styles (Modern/Corporate/Minimal/
   Compact + Contract Staffing/Bajaj/Reliance): left = website, right = email,
   one row with space-between. Optional template footerText sits centered.
   `margin-top: auto` pins this strip to the bottom of the A4 `.invoice-content`
   flex column when the body is shorter than one page. */
.invoice-contact-footer {
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  gap: 0.75em;
  margin-top: auto;
  flex-shrink: 0;
  padding-top: 0.65em;
  border-top: 1px solid #eee;
  font-size: 0.88em;
  color: #333;
  line-height: 1.35;
  width: 100%;
}
.invoice-contact-left {
  text-align: left;
}
.invoice-contact-right {
  text-align: right;
  margin-left: auto;
}
.invoice-contact-footer-center {
  flex: 1;
  text-align: center;
  padding: 0 0.5em;
  font-style: italic;
  color: #4a4a4a;
}

/* ── Signature / DSC reservation (2026-07-23) ──────────────────────────
   ~120px tall dashed box above "Authorised Signatory" so a Digital
   Signature Certificate stamp has clear room and never overlaps totals,
   Total In Words, or the contact footer. Shared across all styles;
   per-style blocks may retint the border only. */
.signature-box {
  width: 15em;
  max-width: 100%;
  min-height: 120px;
  height: 120px;
  margin-top: 1.15rem;
  margin-bottom: 0.35rem;
  border: 1px dashed #b8b8b8;
  border-radius: 0;
  background-color: transparent;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.authorised-signatory-label {
  margin-top: 0.25rem;
}
.signature-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* ══════════════════════════════════════════════════════════════════════
   DESIGN STYLES — v2, professional-polish pass (2026-07-22, same day as
   the ground-up redesign below it replaces). Honest self-critique of that
   first pass, benchmarked against Stripe/QuickBooks/FreshBooks/Zoho/Xero-
   calibre invoices: it was more DIFFERENTIATED than the version before it,
   but leaned on decoration to create that differentiation — solid
   accent-filled badges/headers/total-bands, drop shadows, 8-12px rounded
   "cards" on every section — which reads as template-y/amateur rather
   than professional, no matter how internally consistent each style was.
   Concretely, this pass:
     1. Cut accent-COLOUR usage from 3-5 places per style down to exactly
        1 (Minimal, Compact, Corporate) or 2 closely-related places
        (Modern: title + total figure) — see each block's own comment for
        exactly where. No style fills a background with the accent colour
        anymore; every remaining accent touch is TEXT or a thin RULE.
     2. Removed every box-shadow (all 5 of them lived in Modern) and cut
        Modern's border-radius from 8-12px down to a single, consistent
        4px used in exactly 2 places (table corners, signature box) instead
        of on every section — real invoices from the products above are
        print documents, not floating UI cards.
     3. Removed the solid accent-filled section-label "badges" (Corporate)
        and rounded accent-tinted "pills" (Modern) — section-labels are now
        plain, uncoloured, unfilled small-caps-style text in all 4 styles
        (`color:#767676` shared default), differentiated only by
        letter-spacing/weight per style.
     4. Removed the solid accent-filled Total Amount band (Corporate) and
        accent-tinted Total Amount fill (Modern) — the total is now made
        unmistakably prominent purely via size (1.3em, up from 1.08em),
        weight (already bold via the template's own markup), and added
        vertical padding/whitespace (0.5em top+bottom), matching "weight
        and size alone, not colour-block backgrounds" directly.
     5. Tightened the shared type scale (see the STRUCTURE section above):
        title 1.37em→1.08em, section-label 0.8em→0.72em, party-name
        1.09em→1em, totals-table 0.94em→0.92em — fewer, closer sizes, with
        weight/letter-spacing doing more of the hierarchy work than size.
     6. Fixed a real bug: `.invoice-content`'s per-style padding was
        silently defeated by Bootstrap's `!important` `.p-2` utility class
        in the template the whole time (see that STRUCTURE-section comment)
        — every style rendered with the exact same 0.5rem outer padding
        regardless of what this file said. Fixed at the root, so the
        density differences below (Compact tightest → Corporate moderate →
        Minimal airier → Modern airiest) now actually render.
     7. Letterhead rules thinned from 2-4px down to 1-2px (never darker
        than the accent colour itself) — "thin hairline, not heavy/dark".

   2026-07-22 v3 (differentiation pass — user feedback: "Corporate is
   perfect, the other 3 look too similar to each other and to Corporate").
   CORPORATE IS THE APPROVED REFERENCE AND IS LEFT COMPLETELY UNCHANGED
   BELOW. The v2 restraint pass above fixed color/decoration overuse but,
   in doing so, made Modern/Minimal/Compact converge on the same quiet
   "just smaller/lighter than Corporate" look instead of having their own
   identities. This pass pushes each of the other 3 harder along
   TYPOGRAPHIC and STRUCTURAL axes (never by re-adding fills/shadows/
   badges) so all 4 are unmistakable side-by-side:
     - Modern: letter-spacing went to exactly 0 (title + table header,
       vs. Corporate's 0.1em/0.04em tracked serif+uppercase) and its
       `.invoice-section` rows now carry `line-height: 1.65` (vs.
       Corporate's unchanged ~1.4) — genuinely airier, not marginally so.
       Its dividers (`.invoice-section`/`.invoice-col-divider`) AND its
       letterhead rule are now fully removed (`border: none`) — Corporate
       keeps visible rules throughout, so "rule vs. no rule" is an
       unmissable structural difference, not a shade of grey. Table
       header text dropped from bold-uppercase-dark to
       `font-weight: 500`, `color: #5b6472`, normal case.
     - Minimal: root font-family changed from Georgia (matching
       Corporate's title font) to `'Times New Roman', Times, Georgia,
       serif` — a different serif letterform from Corporate's, not just
       "also a serif". The title is now `text-transform: lowercase` +
       `font-style: italic` + `letter-spacing: 0.24em` (vs. Corporate's
       uppercase/bold/0.1em) — "tax invoice", not "TAX INVOICE". The
       table header is now italic/normal-case (`font-style: italic;
       text-transform: none`) instead of the bold-uppercase treatment it
       previously shared with Corporate. `.invoice-section` now carries
       `line-height: 1.5` for extra editorial air.
     - Compact: every letter-spacing value is now explicitly `0` (title,
       section-label) — zero typographic ambition, on purpose. Its
       `.invoice-line-items-table`/`.invoice-totals-table` amount columns
       (`td.text-right`) now render in `'Courier New', Courier, monospace`
       — a genuinely different, ledger-style numeral treatment none of
       the other 3 styles use. `.invoice-section`/`.invoice-footer`
       line-height tightened further to 1.15 (from 1.2), and the
       signature box is explicitly `border-style: solid` (vs. Minimal's
       dashed) for a plain, functional look.

   Each style keeps its own distinct identity, but now built from
   typography (font stack/weight/letter-spacing/case), line-height/spacing
   density, and *how* restrainedly its one accent touch is deployed — not
   from visual decoration: Corporate = premium/formal (serif masthead,
   crisp thin rules, uppercase small caps, zero colour below the
   letterhead), Modern = SaaS-inspired (system-ui sans, zero tracking, no
   rules anywhere, the airiest line-height, colour confined to the title +
   total figure), Minimal = editorial/monochrome (Times New Roman,
   lowercase italic masthead, italic table header, almost no rules at
   all, colour only on the total figure), Compact = dense/efficient
   (smallest scale, zero letter-spacing, monospace ledger numerals,
   tightest line-height, colour only on the total row).

   PDF/print safety (unchanged from the first pass): every font stack is an
   OS/browser-bundled system font (Georgia/Times New Roman/Arial/the
   standard "system-ui" sans stack) — never `@font-face`/Google Fonts,
   which would add a network dependency that might still be loading (or
   fail outright) the instant html2canvas rasterizes the DOM for PDF
   export. No gradients or CSS filters anywhere. All 4 styles still reuse
   the exact same markup, the table-layout:fixed + percentage-width column
   machinery, and the em-based scaling system from the structure above —
   every rule below is paint only. ══════════════════════════════════════ */

/* ── CORPORATE — premium enterprise: a serif masthead title over a plain
   sans-serif body (the classic formal-letterhead pairing), crisp thin
   rules (never heavy), small-caps-style uppercase labels, and exactly ONE
   accent touch — the letterhead rule + title colour, right at the top of
   the document. Everything below that (section labels, table, totals)
   stays neutral black/grey/white; the "confident, formal" feel comes from
   weight and uppercase tracking, not from colour-filled badges. ── */
.invoice-style-corporate {
  font-family: Arial, Helvetica, sans-serif;
}
.invoice-style-corporate .invoice-content {
  padding: 0.75rem;
}
.invoice-style-corporate .invoice-letterhead {
  border-bottom: 2px solid var(--invoice-primary-color, #12233d);
}
.invoice-style-corporate .invoice-title {
  font-family: Georgia, 'Times New Roman', serif;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: var(--invoice-primary-color, #12233d);
}
.invoice-style-corporate .section-label {
  color: #444;
  font-weight: 700;
  letter-spacing: 0.06em;
}
.invoice-style-corporate .invoice-section,
.invoice-style-corporate .invoice-col-divider {
  border-color: #d9d9d9;
}
.invoice-style-corporate .invoice-line-items-table >>> th,
.invoice-style-corporate .invoice-line-items-table >>> td {
  padding: 0.35rem 0.6rem;
  border-color: #d9d9d9;
}
/* Bold, dark, small-caps-style header with a crisp rule underneath —
   "formal ledger" via weight/case/rule, not a solid colour fill. */
.invoice-style-corporate .invoice-line-items-table >>> thead th {
  background-color: #fafafa;
  color: #1f1f1f;
  font-weight: 700;
  font-size: 0.92em;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  border-bottom: 2px solid #1f1f1f;
}
.invoice-style-corporate .invoice-totals-table >>> td {
  border-color: #d9d9d9;
}
/* Classic ledger "grand total" double rule — no colour, no fill; the
   shared 1.3em size + bold weight already make this the most prominent
   figure on the page (see STRUCTURE section above). */
.invoice-style-corporate .invoice-totals-table >>> .total-amount-row td {
  border-top: 3px double #1f1f1f;
}
.invoice-style-corporate .invoice-billing-period-note {
  border-top: 1px solid #d9d9d9;
  border-bottom: 1px solid #d9d9d9;
}
.invoice-style-corporate .total-in-words,
.invoice-style-corporate .invoice-terms,
.invoice-style-corporate .invoice-contact-footer {
  border-color: #d9d9d9;
  color: #444;
}
.invoice-style-corporate .signature-box {
  border: 1px solid #999;
}

/* ── MODERN — SaaS-inspired (2026-07-22, renamed from "Standard"; reworked
   2026-07-22 v3 for stronger differentiation — see the DESIGN STYLES
   header comment above): a clean system-font sans-serif with letter-
   spacing pinned to exactly 0 everywhere (vs. Corporate's tracked serif
   title + tracked uppercase table header), the airiest rhythm of the 4
   styles (1.65 line-height on every info row; Corporate's is unchanged
   ~1.4), and — structurally, not just tonally — NO rule at all under the
   letterhead or between sections (Corporate keeps visible rules
   throughout); separation here comes purely from whitespace. Retains its
   single 4px corner radius (table header, signature box) as its one
   "SaaS" shape cue. Colour stays confined to exactly 2 closely-related
   places: the title and the Total Amount figure — both plain coloured
   TEXT, never a filled background. ── */
.invoice-style-modern {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  font-size: 1.02em;
}
.invoice-style-modern .invoice-content {
  padding: 1.1rem;
}
/* No rule anywhere in this block — Corporate's equivalents (letterhead
   border, section/col dividers) all stay visible; here, separation is
   whitespace-only. */
.invoice-style-modern .invoice-letterhead {
  border-bottom: none;
}
.invoice-style-modern .invoice-section,
.invoice-style-modern .invoice-col-divider {
  border: none;
}
.invoice-style-modern .invoice-section {
  line-height: 1.65;
}
/* Smaller than Corporate's title (0.95em vs. the shared 1.08em skeleton
   Corporate keeps) and zero tracking (vs. Corporate's 0.1em) — a quiet
   SaaS UI label, not a formal masthead. */
.invoice-style-modern .invoice-title {
  font-size: 0.95em;
  font-weight: 600;
  letter-spacing: 0;
  color: var(--invoice-primary-color, #3457d5);
}
/* Cooler, softer grey than Corporate's neutral #444, tighter tracking
   (0.02em vs. Corporate's 0.06em) — a muted SaaS-UI-style caption. */
.invoice-style-modern .section-label {
  color: #8a94a6;
  letter-spacing: 0.02em;
}
.invoice-style-modern .invoice-line-items-table >>> th,
.invoice-style-modern .invoice-line-items-table >>> td {
  padding: 0.45rem 0.7rem;
  border-color: #ececec;
}
/* Normal case, medium weight, cool grey text on a light tint, zero
   tracking — deliberately the opposite of Corporate's bold/uppercase/
   tracked/dark header, and un-italicised unlike Minimal's. */
.invoice-style-modern .invoice-line-items-table >>> thead th {
  background-color: #f7f8fa;
  color: #5b6472;
  font-weight: 500;
  font-size: 1em;
  text-transform: none;
  letter-spacing: 0;
  border-color: #ececec;
}
.invoice-style-modern .invoice-line-items-table >>> thead th:first-child {
  border-top-left-radius: 4px;
}
.invoice-style-modern .invoice-line-items-table >>> thead th:last-child {
  border-top-right-radius: 4px;
}
.invoice-style-modern .invoice-totals-table >>> td {
  border-color: #ececec;
  padding: 0.3rem 0.7rem;
}
/* Plain rule + coloured figure only — no fill (unchanged principle). */
.invoice-style-modern .invoice-totals-table >>> .total-amount-row td {
  border-top: 2px solid var(--invoice-primary-color, #3457d5);
}
.invoice-style-modern .invoice-totals-table >>> .total-amount-row td:last-child {
  color: var(--invoice-primary-color, #3457d5);
}
.invoice-style-modern .signature-box {
  border: 1px dashed #c5cad3;
  border-radius: 4px;
  /* Extra air above the DSC box — Modern's totals sit close otherwise. */
  margin-top: 1.5rem;
}
.invoice-style-modern .invoice-contact-footer {
  border-color: #ececec;
  color: #5b6472;
}
.invoice-style-modern .total-in-words {
  margin-bottom: 0.85rem;
}

/* ── MINIMAL — editorial, monochrome (reworked 2026-07-22 v3 for stronger
   differentiation — see the DESIGN STYLES header comment above): Times
   New Roman (a genuinely different serif letterform from Corporate's
   Georgia, not just "also a serif"), a LOWERCASE, italic, widely-tracked
   title ("tax invoice", not "TAX INVOICE") in place of Corporate's bold
   uppercase masthead, and an italic, normal-case table header instead of
   Corporate's bold-uppercase one — the two styles now share almost no
   typographic DNA despite both being built on a formal serif base.
   Structurally, this stays the most rule-free of the 4: only the
   letterhead hairline and the table's single header-underline survive,
   and even the Total row's own rule is a single line (Corporate's is a
   double rule) so the "final figure" moment stays quiet — the coloured
   value text (its one accent touch) does the work instead. ── */
.invoice-style-minimal {
  font-family: 'Times New Roman', Times, Georgia, serif;
}
.invoice-style-minimal .invoice-content {
  padding: 0.85rem;
}
.invoice-style-minimal .invoice-section,
.invoice-style-minimal .invoice-col-divider,
.invoice-style-minimal .total-in-words,
.invoice-style-minimal .invoice-terms {
  border-color: transparent;
}
.invoice-style-minimal .invoice-contact-footer {
  border-color: #ddd;
  color: #666;
}
.invoice-style-minimal .invoice-section {
  line-height: 1.5;
}
.invoice-style-minimal .invoice-letterhead {
  border-bottom: 1px solid #ddd;
}
/* Lowercase + italic + wide tracking + regular (non-bold) weight — a
   quiet editorial "flag", the direct opposite of Corporate's bold
   uppercase serif masthead. */
.invoice-style-minimal .invoice-title {
  font-weight: 400;
  font-style: italic;
  letter-spacing: 0.24em;
  text-transform: lowercase;
  color: #1a1a1a;
}
.invoice-style-minimal .section-label {
  color: #999;
  font-style: italic;
  letter-spacing: 0.12em;
}
.invoice-style-minimal .invoice-line-items-table >>> th,
.invoice-style-minimal .invoice-line-items-table >>> td {
  padding: 0.45rem 0.5rem;
}
/* Italic, normal-case serif header — deliberately NOT the uppercase
   treatment Corporate uses, so the one structural device the two styles
   still share (a rule-under-header table) reads completely differently. */
.invoice-style-minimal .invoice-line-items-table >>> thead th {
  background-color: transparent;
  border-top: none;
  border-left: none;
  border-right: none;
  border-bottom: 2px solid #1a1a1a;
  color: #1a1a1a;
  font-weight: 400;
  font-style: italic;
  text-transform: none;
  letter-spacing: 0;
}
.invoice-style-minimal .invoice-line-items-table >>> tbody td {
  border-left: none;
  border-right: none;
  border-top: none;
  border-bottom: 1px solid #eee;
}
.invoice-style-minimal .invoice-totals-table >>> td {
  border-left: none;
  border-right: none;
  border-top: none;
  border-bottom: 1px solid #eee;
}
/* A single bracketing rule top+bottom (not Corporate's double rule) — the
   quiet, monochrome "final figure" moment; the italic coloured value cell
   below is the only emphasis. */
.invoice-style-minimal .invoice-totals-table >>> .total-amount-row td {
  border-top-color: #1a1a1a;
  border-bottom: 1px solid #1a1a1a;
}
.invoice-style-minimal .invoice-totals-table >>> .total-amount-row td:last-child {
  color: var(--invoice-primary-color, #1a1a1a);
  font-style: italic;
}
.invoice-style-minimal .invoice-billing-period-note {
  border-top: 1px solid #ddd;
  border-bottom: 1px solid #ddd;
}
.invoice-style-minimal .signature-box {
  border: 1px dashed #ccc;
}

/* ── COMPACT — space-efficient, built for long invoices: the smallest
   root font-size of the 4 styles, tight table/cell padding, and the
   tightest `.invoice-content` outer margin — the only one of the 4 that
   was already correctly this dense before the `.invoice-content` padding
   fix (its density came mostly from font-size + table-cell padding, which
   were never blocked by the `!important` bug). Deliberately the least
   decorated: no filled tags, no rounded corners — the accent colour
   appears in exactly one place, the Total Amount row's rule + figure.

   Reworked 2026-07-22 v3 for stronger differentiation — see the DESIGN
   STYLES header comment above: letter-spacing is now explicitly ZERO
   everywhere (title, section-label — no tracking at all, vs. Corporate's
   0.1em/0.06em) — purely functional, no typographic ambition — and every
   amount column (line items + totals) now renders in a monospace
   ledger-style typeface instead of the proportional sans/serif every
   other style uses, the single clearest "dense, no-nonsense ledger"
   signal on the page. ── */
/* Still denser than the other styles, but kept at a legible print size
   (≈9.5–10pt on the 11pt A4 base) — never sub-8pt “thumbnail” text. */
.invoice-style-compact {
  font-size: 0.9em;
}
.invoice-style-compact .invoice-content {
  padding: 0.45rem;
}
.invoice-style-compact .invoice-letterhead {
  border-bottom: 1px solid #ccc;
}
.invoice-style-compact .invoice-title {
  font-size: 0.9em;
  font-weight: 600;
  letter-spacing: 0;
}
.invoice-style-compact .section-label {
  color: #555;
  letter-spacing: 0;
}
.invoice-style-compact .invoice-line-items-table >>> th,
.invoice-style-compact .invoice-line-items-table >>> td {
  padding: 0.15rem 0.4rem;
}
/* Ledger-style monospace numerals on every amount column — the one
   distinctly "functional ledger" signal none of the other 3 styles use.
   Courier New is a universal, pre-installed system font (no network
   dependency, safe for html2canvas). */
.invoice-style-compact .invoice-line-items-table >>> td.text-right,
.invoice-style-compact .invoice-totals-table >>> td.text-right {
  font-family: 'Courier New', Courier, monospace;
}
.invoice-style-compact .invoice-totals-table >>> td {
  padding: 0.15rem 0.5rem;
}
.invoice-style-compact .invoice-totals-table >>> .total-amount-row td {
  border-top-color: var(--invoice-primary-color, #1f1f1f);
}
.invoice-style-compact .invoice-totals-table >>> .total-amount-row td:last-child {
  color: var(--invoice-primary-color, #1f1f1f);
}
.invoice-style-compact .invoice-billing-period-note {
  border-top: 1px dashed #ccc;
  border-bottom: 1px dashed #ccc;
}
.invoice-style-compact .invoice-section,
.invoice-style-compact .invoice-footer {
  line-height: 1.15;
}
.invoice-style-compact .signature-box {
  border-style: solid;
  border-color: #ccc;
  /* Compact stays denser elsewhere, but DSC still needs ≥100px stamp room. */
  min-height: 110px;
  height: 110px;
  margin-top: 1rem;
}
.invoice-style-compact .invoice-contact-footer {
  /* Keep shared `margin-top: auto` so the strip stays at page bottom. */
  color: #555;
}

/* ── CONTRACT STAFFING (2026-07-23, layout-fidelity pass) ──
   Faithful implementation of sattva-invoice-contract-staffing.html.
   Colour palette from that reference: navy #1E2A5A, teal #12A5B8,
   soft #F4F6FA, line #D9DCE4, cream #FFFBEA.

   Structure (style-conditional markup, see `isContractStaffing`):
     brandbar → .cs-parties-grid (2 bordered boxes + gap) → .cs-meta
     (right-aligned Ref/Invoice No/Date) → line-items table → period-note
     → .cs-bottom (reg+bank | totals) → .cs-sign → .cs-footer

   Required change vs. the reference file (explicit product direction):
   LIGHT table header (`#F4F6FA` + dark navy text), not the reference's
   dark-navy-fill/white-text thead.

   Fonts: system stacks only (no Google Fonts) for html2canvas safety. ── */
.invoice-style-contract-staffing {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  color: #232323;
  line-height: 1.5;
}
.invoice-style-contract-staffing .invoice-content {
  padding: 1.75rem 2rem;
}
.invoice-style-contract-staffing .invoice-letterhead {
  border-bottom: none;
  /* Shared markup carries Bootstrap `pb-2 mb-2` (!important). Without
     matching !important here those utilities reopen a large dead gap
     under the logo / "TAX INVOICE" row before the parties grid. */
  padding-bottom: 0 !important;
  margin-bottom: 0 !important;
}
.invoice-style-contract-staffing .invoice-letterhead-logo {
  height: 2.6em;
  max-width: 120px;
}
/* "Tax Invoice" bordered pill — reference `.doc-type`. */
.invoice-style-contract-staffing .invoice-title {
  font-size: 0.72em;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--invoice-primary-color, #1E2A5A);
  border: 1.5px solid var(--invoice-primary-color, #1E2A5A);
  padding: 0.4em 1em;
}
.invoice-style-contract-staffing .section-label {
  color: #12A5B8;
  font-weight: 700;
  letter-spacing: 0.14em;
  font-size: 0.72em;
  text-transform: uppercase;
  margin-bottom: 0.35em;
}
.invoice-style-contract-staffing .party-name {
  color: #1E2A5A;
  font-weight: 700;
  font-size: 1em;
}

/* Parties — reference `.grid` / `.box` with real column gap. */
.invoice-style-contract-staffing .cs-parties-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 18px 28px;
  /* Was 1.4rem — stacked with letterhead pb/mb to leave a large empty band
     under "TAX INVOICE". Keep a short clean breath only. */
  margin-top: 0.55rem;
}
.invoice-style-contract-staffing .cs-box {
  border: 1px solid #D9DCE4;
  padding: 0.85em 1em;
}
.invoice-style-contract-staffing .cs-box-addr {
  font-size: 0.9em;
  color: #555;
  margin: 0.2em 0 0.45em;
  line-height: 1.55;
}
.invoice-style-contract-staffing .cs-kv {
  display: flex;
  justify-content: space-between;
  gap: 0.75em;
  border-bottom: 1px dotted #D9DCE4;
  padding: 0.25em 0;
  font-size: 0.9em;
}
.invoice-style-contract-staffing .cs-kv:last-child {
  border-bottom: none;
}
.invoice-style-contract-staffing .cs-kv b {
  color: #1E2A5A;
  font-weight: 700;
  text-align: right;
}

/* Meta — reference `.meta` right-aligned cell bar. */
.invoice-style-contract-staffing .cs-meta {
  display: flex;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 14px;
  margin-top: 1.15rem;
  margin-bottom: 0.25rem;
}
.invoice-style-contract-staffing .cs-meta-cell {
  background-color: #F4F6FA;
  border-top: 2px solid #12A5B8;
  padding: 0.55em 1.1em;
  text-align: center;
}
.invoice-style-contract-staffing .cs-meta-cell label {
  display: block;
  font-size: 0.72em;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: #888;
  margin-bottom: 0.15em;
  font-weight: 700;
}
.invoice-style-contract-staffing .cs-meta-val {
  font-weight: 700;
  color: #1E2A5A;
  font-size: 1em;
}

.invoice-style-contract-staffing .invoice-line-items-table {
  margin-top: 1.35rem;
  /* Bottom gap is owned by `.cs-bottom` / billing-period note — avoid stacking. */
  margin-bottom: 0;
}
/* Drop Bootstrap's full grid border so cells match the reference's
   bottom-rule-only table treatment. */
.invoice-style-contract-staffing .invoice-line-items-table.table-bordered,
.invoice-style-contract-staffing .invoice-line-items-table.table-bordered >>> th,
.invoice-style-contract-staffing .invoice-line-items-table.table-bordered >>> td {
  border: none;
  border-bottom: 1px solid #D9DCE4;
}
.invoice-style-contract-staffing .invoice-line-items-table >>> th,
.invoice-style-contract-staffing .invoice-line-items-table >>> td {
  padding: 0.55rem 0.6rem;
  border-color: #D9DCE4;
  font-size: 0.95em;
}
/* Required exception vs. reference: LIGHT header + dark navy text. */
.invoice-style-contract-staffing .invoice-line-items-table >>> thead th {
  background-color: #F4F6FA;
  color: #1E2A5A;
  font-weight: 600;
  font-size: 0.78em;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid #D9DCE4;
}
.invoice-style-contract-staffing .invoice-line-items-table >>> tbody td {
  font-size: 0.75em;
}
.invoice-style-contract-staffing .invoice-line-items-table >>> tbody td.invoice-col-money,
.invoice-style-contract-staffing .invoice-line-items-table >>> tbody td.invoice-col-num,
.invoice-style-contract-staffing .invoice-line-items-table >>> tbody td.text-right {
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}
.invoice-style-contract-staffing .invoice-line-items-table >>> tbody tr:nth-child(even) {
  background-color: #FAFBFD;
}
.invoice-style-contract-staffing .invoice-billing-period-note {
  background-color: #F4F6FA;
  color: #1E2A5A;
  border-left: 3px solid #12A5B8;
  border-top: none;
  border-bottom: none;
  text-align: center;
  font-weight: 600;
  font-style: normal;
  margin-top: 0.85rem;
  padding: 0.5em 0.6em;
}

/* Bottom — reference `.bottom` flex: reg+bank left, totals right. */
.invoice-style-contract-staffing .cs-bottom {
  display: flex;
  gap: 36px;
  margin-top: 1.25rem;
  align-items: flex-start;
}
.invoice-style-contract-staffing .cs-reg {
  flex: 1;
  min-width: 0;
  font-size: 0.9em;
}
.invoice-style-contract-staffing .cs-kvrow {
  display: flex;
  padding: 0.3em 0;
  border-bottom: 1px dotted #D9DCE4;
}
.invoice-style-contract-staffing .cs-kvrow > span:first-child {
  width: 150px;
  flex-shrink: 0;
  color: #12A5B8;
  font-weight: 700;
  font-size: 0.78em;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  padding-top: 0.1em;
}
.invoice-style-contract-staffing .cs-kvrow > span:last-child {
  font-weight: 600;
  color: #333;
}
.invoice-style-contract-staffing .cs-bank,
.invoice-style-contract-staffing .invoice-bank-box {
  margin-top: 0.85rem;
  background-color: #FFFBEA;
  border: 1px solid #EFE3B0;
  padding: 0.7em 0.85em;
  font-size: 0.9em;
  line-height: 1.8;
}
.invoice-style-contract-staffing .cs-bank b {
  color: #1E2A5A;
}
.invoice-style-contract-staffing .cs-totals {
  width: 300px;
  min-width: 12.5em;
  flex-shrink: 0;
  overflow: visible;
  box-sizing: border-box;
}
.invoice-style-contract-staffing .cs-trow {
  display: flex;
  justify-content: space-between;
  padding: 0.4em 0.7em;
  font-size: 0.95em;
  border-bottom: 1px solid #D9DCE4;
}
.invoice-style-contract-staffing .cs-trow > span:first-child {
  color: #666;
}
.invoice-style-contract-staffing .cs-grand {
  background-color: #F0F2F5;
  color: #2C3345;
  font-weight: 600;
  font-size: 0.95em;
  padding: 0.55em 0.75em;
  display: flex;
  justify-content: space-between;
  margin-top: 0.35em;
  border: 1px solid #D9DCE4;
}
.invoice-style-contract-staffing .cs-words {
  margin-top: 0.5em;
  font-size: 0.85em;
  color: #666;
  font-style: italic;
  text-align: right;
}

.invoice-style-contract-staffing .invoice-terms {
  border-color: #D9DCE4;
  margin-top: 0.75rem;
}

/* Signature — reference `.sign` + shared DSC box. */
.invoice-style-contract-staffing .cs-sign {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  margin-top: 1.75rem;
  gap: 1.5rem;
}
.invoice-style-contract-staffing .cs-forco {
  font-size: 0.9em;
}
.invoice-style-contract-staffing .cs-forco b {
  color: #1E2A5A;
}
.invoice-style-contract-staffing .cs-signline {
  text-align: center;
  font-size: 0.9em;
  color: #444;
}
.invoice-style-contract-staffing .cs-signline .signature-box {
  margin-left: auto;
  margin-right: auto;
  border-color: #D9DCE4;
}
.invoice-style-contract-staffing .cs-signline-rule {
  width: 15em;
  max-width: 100%;
  border-top: 1.5px solid #232323;
  margin: 0.35em auto;
}

/* Footer — shared contact lines, CS palette. */
.invoice-style-contract-staffing .cs-footer {
  border-color: #D9DCE4;
  color: #888;
}

/* No viewport media reflow: the sheet is always a fixed A4 width; narrow
   screens scale it with transform instead of stacking columns (which would
   desync the live preview from the downloaded A4 PDF). */

/* ── BAJAJ / OLD Template (2026-07-23, fidelity pass) — SH1693 ──
   Source HTML is pdf2htmlEX (form PNG + absolute text). Visual rules from
   that sheet:

   • Logo top-right, outside the main content area
   • No outer document frame (party boxes retain their own borders)
   • Centered "Tax Invoice" title
   • Soft yellow highlight cells ≈ #FFF9C4 (Invoice No / Date / GST / SAC /
     PAN / bank box / Total Amount value / For-company bar)
   • Table header light grey #E8E8E8, soft 1px #c4c4c4 grid
   • Faint centered logo watermark (shared .invoice-logo-watermark)
   • Contact footer under the frame (not inside the border): website left /
     email right on one row
   Fonts: Arial/Helvetica (html2canvas-safe; reference used embedded WOFF). ── */
.invoice-style-bajaj {
  font-family: Arial, Helvetica, 'Segoe UI', sans-serif;
  color: #000;
  line-height: 1.35;
  border-radius: 0 !important;
  border-color: #000 !important;
  padding-top: 2.6rem; /* room for top-right logo outside the frame */
}
.invoice-style-bajaj .invoice-logo-watermark {
  opacity: 0.1;
  width: 48%;
  max-width: 300px;
}
.invoice-style-bajaj .invoice-content {
  padding: 0.35rem 0.55rem 0.15rem;
}
.invoice-style-bajaj .bj-letterhead {
  position: absolute;
  top: 0.35rem;
  right: 0.75rem;
  z-index: 2;
  margin: 0;
  padding: 0;
  border: none;
}
.invoice-style-bajaj .bj-logo.invoice-letterhead-logo {
  height: 2.15em;
  max-width: 110px;
}
.invoice-style-bajaj .bj-sheet {
  border: none;
  padding: 0.55rem 0.65rem 0.7rem;
  background: #fff;
}
.invoice-style-bajaj .bj-title,
.invoice-style-bajaj .invoice-title {
  display: block;
  text-align: center;
  font-weight: 700;
  font-size: 1.15em;
  color: #000;
  letter-spacing: 0.02em;
  text-transform: none;
  margin: 0 0 0.55rem;
  padding: 0;
}
.invoice-style-bajaj .bj-top {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.65rem 0.85rem;
  /* Gap after From|To is owned by `.bj-items-block` margin-top. */
  margin-bottom: 0;
  /* Stretch both party tables to the taller side so From|To boxes stay equal height. */
  align-items: stretch;
}
.invoice-style-bajaj .bj-party-table {
  width: 100%;
  height: 100%;
  border: 1px solid #c4c4c4;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.88em;
  box-sizing: border-box;
}
.invoice-style-bajaj .bj-party-label-col {
  width: 34%;
}
.invoice-style-bajaj .bj-party-val-col {
  width: 66%;
}
.invoice-style-bajaj .bj-party-head {
  text-align: left;
  font-weight: 700;
  padding: 0.55em 0.55em 0.35em;
  border-bottom: 1px solid #c4c4c4;
  background: #fff;
}
.invoice-style-bajaj .bj-to-label,
.invoice-style-bajaj .bj-from-label {
  font-weight: 700;
  font-size: 0.95em;
  margin: 0;
  padding: 0;
}
.invoice-style-bajaj .bj-party-table td {
  /* Soft grid — matches line-items / GST meta (lightweight vs heavy black). */
  border: 1px solid #c4c4c4;
  vertical-align: top;
  padding: 0.35em 0.55em;
}
.invoice-style-bajaj .bj-party-label {
  /* Same weight on every label row (Company → Date); avoid mixed bold. */
  font-weight: 700 !important;
  text-align: left;
  border-right: 1px solid #c4c4c4;
  background: #fff;
  white-space: normal;
  word-break: normal;
  overflow-wrap: break-word;
}
.invoice-style-bajaj .bj-party-val {
  font-weight: 700;
  text-align: left;
}
.invoice-style-bajaj .bj-hl {
  background-color: #FFF9C4 !important; /* soft pale yellow (was #FFFF99) */
}
.invoice-style-bajaj .bj-hl-box {
  display: block;
  font-weight: 700 !important;
}
/* Whole items+totals section: space before first header row and after
   Total in Words (before `.bj-bottom` bank/sign). Inner tables stay flush. */
.invoice-style-bajaj .bj-items-block {
  margin-top: 0.75rem;
  margin-bottom: 0.85rem;
}
.invoice-style-bajaj .bj-items-table {
  /* Override shared `.invoice-line-items-table` 0.5rem margins — outer gap
     lives on `.bj-items-block`; keep a flush sheet seam into Sub-Total. */
  margin-top: 0;
  margin-bottom: 0;
}
/* Match `.bj-sum-table`: 1px #000 grid on the table and every cell.
   BV2 `bordered` adds `.table-bordered` on the inner <table>; custom classes
   must use `table-class` (not `class`) to land on that same element. Use
   descendant (not `>`) so multi-table mode still matches — tables sit inside
   `.invoice-li-multi-tables > .invoice-li-table-group`, not as direct children
   of `.bj-items-block`. rtl:ignore keeps postcss-rtl from gating borders. */
/* rtl:begin:ignore */
.invoice-style-bajaj .bj-items-block table.table-bordered,
.invoice-style-bajaj .bj-items-block table.table-bordered >>> th,
.invoice-style-bajaj .bj-items-block table.table-bordered >>> td,
.invoice-style-bajaj .bj-items-block table.bj-items-table,
.invoice-style-bajaj .bj-items-block table.bj-items-table >>> th,
.invoice-style-bajaj .bj-items-block table.bj-items-table >>> td,
.invoice-style-bajaj .invoice-li-table-group table.table-bordered,
.invoice-style-bajaj .invoice-li-table-group table.table-bordered >>> th,
.invoice-style-bajaj .invoice-li-table-group table.table-bordered >>> td {
  border: 1px solid #c4c4c4 !important;
}
.invoice-style-bajaj .bj-items-block table.table-bordered,
.invoice-style-bajaj .bj-items-block table.bj-items-table,
.invoice-style-bajaj .invoice-li-table-group table.table-bordered {
  border-collapse: collapse !important;
}
/* rtl:end:ignore */
.invoice-style-bajaj .invoice-line-items-table >>> th,
.invoice-style-bajaj .invoice-line-items-table >>> td {
  padding: 0.3rem 0.35rem;
  font-size: 0.85em;
  color: #000;
  background: #fff;
}
.invoice-style-bajaj .invoice-line-items-table >>> thead th,
.invoice-style-bajaj .invoice-line-items-table >>> .invoice-li-repeat-header-row > th {
  background-color: #E8E8E8 !important;
  color: #000;
  font-weight: 700;
  font-size: 0.78em;
  text-align: center;
  text-transform: none;
  letter-spacing: 0;
  border-bottom: 1px solid #c4c4c4 !important;
}
.invoice-style-bajaj .invoice-line-items-table >>> thead th.invoice-col-text {
  text-align: left !important;
}
.invoice-style-bajaj .invoice-line-items-table >>> thead th.invoice-col-num {
  text-align: center !important;
}
.invoice-style-bajaj .invoice-line-items-table >>> thead th.invoice-col-money {
  text-align: right !important;
}
/* Values smaller than headers (thead stays 0.78em). */
.invoice-style-bajaj .invoice-line-items-table >>> tbody td {
  font-size: 0.72em;
}
.invoice-style-bajaj .invoice-line-items-table >>> caption.invoice-li-group-title,
.invoice-style-bajaj .invoice-line-items-table >>> .invoice-li-group-title {
  background: transparent !important;
  font-weight: 700;
  text-align: left;
  font-size: 0.78em;
  color: #000;
}
/* Keep Bajaj body amounts single-line inside the double-border sheet. */
.invoice-style-bajaj .invoice-line-items-table >>> tbody td.invoice-col-money,
.invoice-style-bajaj .invoice-line-items-table >>> tbody td.invoice-col-num,
.invoice-style-bajaj .invoice-line-items-table >>> tbody td.text-right,
.invoice-style-bajaj .invoice-line-items-table >>> tbody td.text-center {
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
}
.invoice-style-bajaj .bj-sum-table {
  width: 100%;
  border-collapse: collapse;
  /* Space between line-item rows and Sub-Total / totals block. */
  margin-top: 0.65rem;
  margin-bottom: 0;
  /* Match Bill From / Bill To party table — softer than prior 0.9em. */
  font-size: 0.88em;
}
.invoice-style-bajaj .bj-sum-table td {
  border: 1px solid #c4c4c4;
  /* Same cell padding as `.bj-party-table` (2nd screenshot formatting). */
  padding: 0.35em 0.55em;
  vertical-align: top;
}
.invoice-style-bajaj .bj-sum-label {
  text-align: right;
  font-weight: 700;
  width: 78%;
}
.invoice-style-bajaj .bj-sum-val {
  text-align: right;
  white-space: nowrap;
  width: 22%;
  min-width: 7em;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  overflow: visible;
  box-sizing: border-box;
}
.invoice-style-bajaj .bj-sum-total .bj-sum-val {
  font-weight: 700;
}
.invoice-style-bajaj .bj-sum-words-text {
  text-align: left;
  font-size: 0.92em;
}
.invoice-style-bajaj .bj-sum-desc-text,
.invoice-style-bajaj .bj-sum-desc-text strong {
  text-align: center;
  font-family: inherit;
  font-weight: 700 !important;
  line-height: 1.4;
  white-space: pre-wrap;
}
.invoice-style-bajaj .bj-sum-desc-text {
  font-size: 0.88em;
}
.invoice-style-bajaj .bj-bottom {
  display: grid;
  grid-template-columns: 1.05fr 1fr;
  gap: 0.65rem 0.85rem;
  margin-top: 0;
  align-items: start;
}
.invoice-style-bajaj .bj-terms {
  margin: 0.35rem 0.35rem 0.5rem;
  padding: 0.25rem 0.35rem 0.4rem;
  border-bottom: 1px solid #222;
  font-size: 0.82em;
}
.invoice-style-bajaj .bj-reg {
  /* Match Bill From / Bill To party table size (0.88em) — do not nest-shrink. */
  font-size: 0.88em;
  min-width: 0;
}
.invoice-style-bajaj .bj-for {
  font-weight: 700;
  padding: 0.35em 0.5em;
  border: 1px solid #c4c4c4;
  margin-bottom: 0.35rem;
}
.invoice-style-bajaj .bj-sign {
  text-align: center;
  margin: 0.25rem 0 0.45rem;
}
/* DSC / wet-signature reserve: open white space only — no box outline
   (reference SH1693 leaves this area blank above Authorised Signatory). */
.invoice-style-bajaj .bj-sign .signature-box {
  margin: 0.35rem auto 0.35rem;
  border: none;
  background: transparent;
  box-shadow: none;
  min-height: 110px;
  height: 110px;
}
.invoice-style-bajaj .bj-sign-label {
  font-weight: 700;
  font-size: 0.9em;
  border-top: 1px solid #c4c4c4;
  padding-top: 0.3em;
  display: inline-block;
  min-width: 160px;
}
/* Fixed 2-col meta table — one vertical border across GST / SAC / LUT / PAN.
   Mirrors `.bj-party-table` so borders survive preview scale + html2canvas. */
/* rtl:begin:ignore */
.invoice-style-bajaj .bj-kv-table {
  width: 100%;
  border: 1px solid #c4c4c4;
  border-collapse: collapse !important;
  table-layout: fixed;
  box-sizing: border-box;
  font-size: 1em; /* inherits `.bj-reg` 0.88em → same as party labels */
}
.invoice-style-bajaj .bj-kv-label-col {
  width: 48%;
}
.invoice-style-bajaj .bj-kv-val-col {
  width: 52%;
}
.invoice-style-bajaj .bj-kv-table td {
  border: 1px solid #c4c4c4 !important;
  padding: 0.35em 0.55em;
  vertical-align: top;
  box-sizing: border-box;
}
.invoice-style-bajaj .bj-kv-label {
  width: 48%;
  font-weight: 700;
  white-space: nowrap;
  background: #fff;
}
.invoice-style-bajaj .bj-kv-val {
  width: 52%;
  font-weight: 700;
}
/* rtl:end:ignore */
.invoice-style-bajaj .bj-bank {
  border: 1px solid #c4c4c4;
  padding: 0.5em 0.65em;
  font-size: 0.88em;
  line-height: 1.65;
  min-height: 9em;
}
.invoice-style-bajaj .bj-bank-title {
  font-weight: 700;
  margin-bottom: 0.25em;
}
.invoice-style-bajaj .bj-bank span {
  font-weight: 700;
}
.invoice-style-bajaj .bj-footer {
  /* Same shared row as every other style: website left / email right;
     thin top rule + `margin-top: auto` from `.invoice-contact-footer`. */
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #404040;
  font-size: 0.85em;
}
/* No viewport media reflow — see contract-staffing note above. */

/* ── RELIANCE (2026-07-23, fidelity pass) — SH1690 formal GST sheet ──
   Source HTML is pdf2htmlEX: a form-line PNG (`bi`) + absolute-positioned
   text overlays — there is NO portable semantic stylesheet to copy. Rebuilt
   as real HTML tables that reproduce that form's visual rules:

   Palette: white ground, 1px #000 grid, light-blue header fill #D9E9F3
   (sampled from the form PNG), Arial/Helvetica system stack
   (html2canvas-safe; reference embedded proprietary WOFF faces).

   Structure: logo|seller|Original → Tax Invoice bar → meta (PO + Place of
   Supply on the right) → Bill/Ship → line-items → Total bar → words|tax
   stack → bank | Common Seal | cert+open signatory → Notes (MSME).

   Unavoidable gaps vs pixel PDF: digital-signature stamp overlay, exact
   embedded font metrics, Transport/Vehicle wizard fields (shown as —).

   Watermark: shared `.invoice-logo-watermark` (icon-only, straight, faint)
   — previously hidden; now shown like every other style. ── */
.invoice-style-reliance {
  font-family: Arial, Helvetica, 'Segoe UI', sans-serif;
  color: #000;
  line-height: 1.35;
  border-radius: 0 !important;
  border-color: #000 !important;
}
.invoice-style-reliance .invoice-content {
  padding: 0.45rem 0.55rem;
}
.invoice-style-reliance .rl-form {
  width: 100%;
}
.invoice-style-reliance .rl-form-cont {
  margin-top: -1px; /* collapse against previous sheet edge */
}
.invoice-style-reliance .rl-sheet {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
  font-size: 0.9em;
  color: #000;
  background: #fff;
}
.invoice-style-reliance .rl-sheet td {
  border: 1px solid #000;
  padding: 0.2em 0.35em;
  vertical-align: top;
}
.invoice-style-reliance .rl-col-a { width: 18%; }
.invoice-style-reliance .rl-col-b { width: 47%; }
.invoice-style-reliance .rl-col-c { width: 35%; }
.invoice-style-reliance .rl-hdr-logo {
  text-align: center;
  vertical-align: middle;
  padding: 0.35em;
}
.invoice-style-reliance .invoice-letterhead-logo {
  height: 2.35em;
  max-width: 88px;
}
.invoice-style-reliance .rl-hdr-co {
  font-size: 0.95em;
  line-height: 1.3;
  text-align: center;
  vertical-align: middle;
}
.invoice-style-reliance .rl-co-name {
  font-weight: 700;
  font-size: 1.05em;
  letter-spacing: 0.01em;
}
.invoice-style-reliance .rl-co-addr,
.invoice-style-reliance .rl-co-meta {
  font-size: 0.9em;
}
/* Beat `.rl-sheet td { vertical-align: top }` so the label is cell-centered */
.invoice-style-reliance .rl-sheet td.rl-hdr-orig {
  text-align: center;
  vertical-align: middle;
  font-weight: 700;
  font-size: 0.95em;
}
/* Override global `.invoice-title { display: inline-block }` so the light-blue
   Tax Invoice bar is a full-width table cell with centered text. */
.invoice-style-reliance .rl-title-bar,
.invoice-style-reliance .invoice-title {
  display: table-cell !important;
  width: 100%;
  background-color: #D9E9F3;
  text-align: center;
  font-weight: 700;
  font-size: 1.05em;
  letter-spacing: 0.04em;
  padding: 0.28em 0.4em !important;
  text-transform: none;
  color: #000;
  margin: 0;
  vertical-align: middle;
}
/* Blank bordered breathing-room rows (~22px). Empty cells keep the same
   black grid borders as the Reliance sheet so gaps still read as table rows. */
.invoice-style-reliance .rl-spacer-cell,
.invoice-style-reliance .rl-blank-row > td {
  height: 22px;
  padding: 0 !important;
  line-height: 22px;
  font-size: 0;
  background: #fff !important;
  border: 1px solid #000 !important;
  vertical-align: middle !important;
}
.invoice-style-reliance .rl-spacer-table {
  margin-top: -1px;
  width: 100%;
}
.invoice-style-reliance .rl-words-spacer {
  display: none;
}
.invoice-style-reliance .rl-words-cell {
  background: #fff;
  vertical-align: top;
}
.invoice-style-reliance .rl-words-cell .rl-words-label {
  font-weight: 700;
  margin: -0.2em -0.35em 0;
  padding: 0.22em 0.35em;
  background-color: #D9E9F3;
  border-bottom: 1px solid #000;
}
.invoice-style-reliance .rl-words-val {
  font-size: 0.95em;
  line-height: 1.35;
  min-height: 1.6em;
  padding-top: 0.25em;
}
.invoice-style-reliance .rl-meta-cell {
  font-size: 0.93em;
  padding: 0.32em 0.4em !important;
}
.invoice-style-reliance .rl-meta-cell b {
  font-weight: 700;
}
.invoice-style-reliance .rl-meta-empty {
  border-right: 1px solid #000;
}
/* State | Code nested row: outer cell has no padding; inner table draws
   the vertical divider so State and Code read as proper columns. */
.invoice-style-reliance .rl-meta-split-cell {
  padding: 0 !important;
}
.invoice-style-reliance .rl-meta-split {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}
.invoice-style-reliance .rl-meta-state-col { width: 68%; }
.invoice-style-reliance .rl-meta-code-col { width: 32%; }
.invoice-style-reliance .rl-meta-split td {
  border: none;
  border-right: 1px solid #000;
  vertical-align: middle;
}
.invoice-style-reliance .rl-meta-split td:last-child {
  border-right: none;
}
.invoice-style-reliance .rl-code {
  float: right;
  margin-left: 0.85em;
}
.invoice-style-reliance .rl-party-h {
  background-color: #D9E9F3;
  font-weight: 700;
  text-align: center;
  width: 50%;
  font-size: 0.95em;
  padding: 0.25em 0.4em !important;
}
.invoice-style-reliance .rl-party-body {
  width: 50%;
  font-size: 0.9em;
  line-height: 1.4;
  min-height: 4.5em;
}
.invoice-style-reliance .rl-party-body span {
  font-weight: 700;
}
.invoice-style-reliance .invoice-line-items-table {
  margin: -1px 0 0;
  font-size: 0.88em;
  border-radius: 0;
  box-sizing: border-box;
  overflow: visible;
}
.invoice-style-reliance .invoice-line-items-table.table-bordered,
.invoice-style-reliance .invoice-line-items-table.table-bordered >>> th,
.invoice-style-reliance .invoice-line-items-table.table-bordered >>> td,
.invoice-style-reliance .invoice-li-table-group table.table-bordered,
.invoice-style-reliance .invoice-li-table-group table.table-bordered >>> th,
.invoice-style-reliance .invoice-li-table-group table.table-bordered >>> td {
  border: 1px solid #000 !important;
}
.invoice-style-reliance .invoice-line-items-table.table-bordered,
.invoice-style-reliance .invoice-li-table-group table.table-bordered {
  border-collapse: collapse !important;
}
.invoice-style-reliance .invoice-line-items-table >>> th,
.invoice-style-reliance .invoice-line-items-table >>> td {
  padding: 0.18em 0.22em;
  font-size: 0.92em;
  vertical-align: middle;
  color: #000;
  background: #fff;
}
.invoice-style-reliance .invoice-line-items-table >>> thead th,
.invoice-style-reliance .invoice-line-items-table >>> .invoice-li-repeat-header-row > th {
  background-color: #D9E9F3 !important;
  color: #000;
  font-weight: 700;
  font-size: 0.82em;
  text-align: center !important;
  text-transform: none;
  letter-spacing: 0;
  border-bottom: 1px solid #000 !important;
  vertical-align: middle !important;
}
/* Force every SS5 header cell centered (shared money/num rules use !important right/center). */
.invoice-style-reliance .invoice-line-items-table >>> thead th.invoice-col-money,
.invoice-style-reliance .invoice-line-items-table >>> thead th.invoice-col-num,
.invoice-style-reliance .invoice-line-items-table >>> thead th.invoice-col-text,
.invoice-style-reliance .invoice-line-items-table >>> thead th.invoice-li-th {
  text-align: center !important;
  vertical-align: middle !important;
}
/* Body cells match thead size (0.82em) so amounts/codes are as readable as
   the bold headers. Do not enlarge thead — only lift row values. */
.invoice-style-reliance .invoice-line-items-table >>> tbody td {
  font-size: 0.82em;
  padding: 0.14em 0.14em;
  line-height: 1.25;
}
.invoice-style-reliance .invoice-line-items-table >>> caption.invoice-li-group-title,
.invoice-style-reliance .invoice-line-items-table >>> .invoice-li-group-title {
  background: transparent !important;
  font-weight: 700;
  text-align: left;
  font-size: 0.78em;
  color: #000;
}
.invoice-style-reliance .invoice-line-items-table >>> tbody td.invoice-col-money,
.invoice-style-reliance .invoice-line-items-table >>> tbody td.invoice-col-num,
.invoice-style-reliance .invoice-line-items-table >>> tbody td.text-right {
  white-space: nowrap;
  word-break: normal;
  overflow-wrap: normal;
  font-variant-numeric: tabular-nums;
}
.invoice-style-reliance .invoice-billing-period-note {
  display: none;
}
/* Total row lives in the items table <tfoot> so it shares the column grid. */
.invoice-style-reliance .invoice-line-items-table >>> tfoot td.invoice-li-total-td {
  background-color: #D9E9F3 !important;
  font-weight: 700;
  font-size: 0.82em;
  padding: 0.14em 0.12em;
  vertical-align: middle;
  color: #000;
  border: 1px solid #000 !important;
  overflow: visible;
  box-sizing: border-box;
}
.invoice-style-reliance .invoice-line-items-table >>> tfoot td.invoice-col-money.invoice-li-total-td {
  text-align: right !important;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  overflow: visible;
}
.invoice-style-reliance .invoice-line-items-table >>> tfoot td.invoice-li-total-label {
  text-align: left !important;
}
/* Words | tax sit in the same <tfoot> — do not inherit the blue Total paint. */
.invoice-style-reliance .invoice-line-items-table >>> tfoot td.rl-words-cell,
.invoice-style-reliance .invoice-line-items-table >>> tfoot td.rl-tot-stack {
  background: #fff !important;
  font-weight: 400;
  vertical-align: top !important;
  color: #000;
  border: 1px solid #000 !important;
  overflow: visible;
  box-sizing: border-box;
}
.invoice-style-reliance .invoice-line-items-table >>> tfoot td.rl-words-cell {
  padding: 0.2em 0.35em;
  font-size: 0.92em;
}
.invoice-style-reliance .invoice-line-items-table >>> tfoot td.rl-tot-stack {
  padding: 0 !important;
  font-size: 0.82em;
}
/* Nested tax table must not add a second left rule on top of the
   tfoot cell — `.table-bordered >>> td` would otherwise paint a
   2px-looking bar at IGST Rate. Only thin row dividers inside. */
.invoice-style-reliance .invoice-line-items-table >>> .rl-tot-inner {
  border: none !important;
}
.invoice-style-reliance .invoice-line-items-table >>> .rl-tot-inner td {
  border: none !important;
  border-bottom: 1px solid #000 !important;
  padding: 0.16em 0.18em;
  /* 1em of the stack (0.82em) — same size as the Total row, no second shrink. */
  font-size: 1em;
  background: #fff;
  font-weight: 400;
  white-space: normal;
  line-height: 1.3;
  box-sizing: border-box;
  overflow: visible;
}
.invoice-style-reliance .invoice-line-items-table >>> .rl-tot-inner .rl-tot-strong {
  font-weight: 700;
  background-color: #D9E9F3;
}
.invoice-style-reliance .invoice-line-items-table >>> .rl-tot-inner .rl-tot-val {
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  overflow: visible;
}
.invoice-style-reliance .rl-tot-stack {
  padding: 0 !important;
  vertical-align: top;
  overflow: visible;
  box-sizing: border-box;
}
.invoice-style-reliance .rl-bottom-bank-col { width: 40%; }
.invoice-style-reliance .rl-bottom-seal-col { width: 18%; }
.invoice-style-reliance .rl-bottom-sign-col { width: 42%; }
/* Auto layout so the amount column sizes to ₹99,99,999.99 instead of the
   old 38% fixed slice, which clipped paise after the 0.82em font bump. */
.invoice-style-reliance .rl-tot-inner {
  width: 100%;
  max-width: 100%;
  border: none !important;
  border-collapse: collapse;
  table-layout: auto;
  box-sizing: border-box;
  overflow: visible;
}
.invoice-style-reliance .rl-tot-label-col {
  width: auto;
}
.invoice-style-reliance .rl-tot-val-col {
  width: 1%;
}
.invoice-style-reliance .rl-tot-inner td {
  border: none !important;
  border-bottom: 1px solid #000 !important;
  padding: 0.16em 0.18em;
  font-size: 1em;
  box-sizing: border-box;
  overflow: visible;
}
.invoice-style-reliance .rl-tot-inner tr:last-child td,
.invoice-style-reliance .rl-tot-inner tr.rl-spacer td {
  border-bottom: none !important;
}
/* Totals-stack spacers stay as internal row gaps (not full outer grid cells). */
.invoice-style-reliance .rl-tot-inner .rl-spacer-cell {
  border-left: none !important;
  border-right: none !important;
  border-top: none !important;
  border-bottom: none !important;
  height: 12px;
  line-height: 12px;
}
.invoice-style-reliance .rl-tot-label {
  width: auto;
  text-align: left;
  overflow: visible;
}
.invoice-style-reliance .rl-tot-val {
  width: 1%;
  min-width: 7.25em;
  text-align: right;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  overflow: visible;
  padding-left: 0.12em;
  padding-right: 0.14em;
}
.invoice-style-reliance .rl-tot-strong {
  font-weight: 700;
  background-color: #D9E9F3;
}
.invoice-style-reliance .rl-bank-cell {
  width: 40%;
  font-size: 0.9em;
  line-height: 1.45;
  min-height: 8em;
  vertical-align: top;
}
.invoice-style-reliance .rl-sec-h {
  font-weight: 700;
  margin-bottom: 0.15em;
}
/* Notes row below bank / seal / authorised signatory (MSME lives here). */
.invoice-style-reliance .rl-notes-cell {
  padding: 0.45em 0.55em !important;
  vertical-align: top;
}
.invoice-style-reliance .rl-terms-block {
  margin-top: 0;
}
/* Common Seal — beat `.rl-sheet td { vertical-align: top }` and mirror
   Authorised Signatory: blank reserve + top rule + label at cell bottom. */
.invoice-style-reliance .rl-sheet td.rl-seal-cell {
  width: 18%;
  text-align: center;
  vertical-align: bottom;
  font-size: 0.88em;
  padding: 0.35em !important;
}
.invoice-style-reliance .rl-seal-space {
  width: 100%;
  max-width: 100%;
  min-height: 150px;
  height: 150px;
  margin: 0.45em auto 0.3em;
  box-sizing: border-box;
}
.invoice-style-reliance .rl-seal-label {
  font-weight: 700;
  text-align: center;
  border-top: 1px solid #000;
  margin-top: 0.35em;
  padding-top: 0.25em;
}
.invoice-style-reliance .rl-sign-cell {
  width: 42%;
  text-align: center;
  font-size: 0.9em;
  vertical-align: top;
}
.invoice-style-reliance .rl-rev-charge {
  display: flex;
  justify-content: space-between;
  font-size: 0.9em;
  margin-bottom: 0.35em;
  text-align: left;
}
.invoice-style-reliance .rl-cert {
  font-size: 0.82em;
  font-style: italic;
  text-align: left;
  margin-bottom: 0.4em;
  line-height: 1.3;
}
.invoice-style-reliance .rl-forco {
  font-weight: 700;
  margin-bottom: 0.35em;
  text-align: left;
}
/* Open DSC / wet-signature reserve — taller blank area, no box outline. */
.invoice-style-reliance .rl-sign-area {
  width: 100%;
  max-width: 100%;
  min-height: 150px;
  height: 150px;
  margin: 0.45em auto 0.3em;
  border: none;
  outline: none;
  background: transparent;
  display: flex;
  align-items: center;
  justify-content: center;
  box-sizing: border-box;
}
.invoice-style-reliance .rl-sign-label {
  font-weight: 700;
  border-top: 1px solid #000;
  margin-top: 0.35em;
  padding-top: 0.25em;
}
.invoice-style-reliance .rl-footer {
  /* Shared contact row under the 3-col bank/seal/sign block: website left /
     email right; thin top rule + page-bottom pin from `.invoice-contact-footer`. */
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  color: #333;
}
/* No viewport media reflow/font crush — A4 sheet stays A4; callers scale. */
</style>
